import React, { useEffect, useMemo, useState } from 'react';
import { getTenant, formatCurrency as formatCurrencyFromTokens } from '../../../theme/tokens';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { ArrowLeftIcon, AlertTriangleIcon, CreditCardIcon, CalendarIcon, ClockIcon, FileTextIcon, UploadIcon, CheckCircleIcon, XCircleIcon, DownloadIcon, TrashIcon, InfoIcon, HelpCircleIcon, XIcon as CloseIcon, ChevronLeftIcon, ChevronRightIcon, PenIcon, ChevronUpIcon, ChevronDownIcon, EyeIcon, MoreVerticalIcon, PinIcon, LinkIcon, QrCodeIcon, FileIcon, SmartphoneIcon, TerminalIcon, CopyIcon, CheckIcon } from 'lucide-react';
import { Dispute, DisputeStatus, DisputeStage, DisputeReason, EvidenceDocument } from '../../../shared/types/Dispute';
import { getEvidenceRecommendation } from '../utils/evidenceRecommendations';
import { generateDisputes } from '../utils/disputes';
interface RelatedOrder {
  id: string;
  orderId: string;
  amount: number;
  currency: string;
  createdTime: string;
  status: string;
  matchingCriteria: number;
  totalCriteria: number;
}
interface MatchingCriteriaDetail {
  category: string;
  subtitle: string;
  items: {
    label: string;
    matched: boolean;
  }[];
}
export function DisputeDetail() {
  const {
    t
  } = useTranslation('disputes');
  const navigate = useNavigate();
  const {
    id
  } = useParams();
  const location = useLocation();
  const [showEvidenceForm, setShowEvidenceForm] = useState(false);
  const [showAlert, setShowAlert] = useState(true);
  const [activeTab, setActiveTab] = useState<'payment' | 'related'>('payment');
  const [selectedOrder, setSelectedOrder] = useState<RelatedOrder | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [showMatchingCriteriaTooltip, setShowMatchingCriteriaTooltip] = useState(false);
  const [showReasonTooltip, setShowReasonTooltip] = useState(false);
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [showChallengeModal, setShowChallengeModal] = useState(false);
  const [showChallengeFlow, setShowChallengeFlow] = useState(false);
  const [challengeStep, setChallengeStep] = useState<'reason' | 'evidence' | 'order' | 'customer' | 'other' | 'review'>('reason');
  const [selectedChallengeReason, setSelectedChallengeReason] = useState('purchasing_history');
  const [selectedProductType, setSelectedProductType] = useState('');
  const [noEvidenceChecked, setNoEvidenceChecked] = useState(false);
  const [noOrderEvidenceChecked, setNoOrderEvidenceChecked] = useState(false);
  const [noCustomerEvidenceChecked, setNoCustomerEvidenceChecked] = useState(false);
  const [noOtherEvidenceChecked, setNoOtherEvidenceChecked] = useState(false);
  const [showNoEvidenceWarning, setShowNoEvidenceWarning] = useState(false);
  const [reviewConfirmChecked, setReviewConfirmChecked] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    challengeDetails: true,
    transactionDetails: true,
    otherEvidence: true
  });
  const [showChallengeDetails, setShowChallengeDetails] = useState(false);
  const [showAcceptModal, setShowAcceptModal] = useState(false);
  // State to track if dispute has been accepted
  const [isAccepted, setIsAccepted] = useState(false);
  // State to track if dispute has been challenged (separate from derived isChallenged)
  const [hasBeenChallenged, setHasBeenChallenged] = useState(false);
  // State for adding notes
  const [showAddNote, setShowAddNote] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [merchantNotes, setMerchantNotes] = useState<Array<{
    id: string;
    timestamp: string;
    description: string;
    status: 'note';
    author: string;
    isPinned?: boolean;
  }>>([]);
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  const [editingNoteText, setEditingNoteText] = useState('');
  const [showNoteMenu, setShowNoteMenu] = useState<string | null>(null);
  const [copiedOrderId, setCopiedOrderId] = useState<string | null>(null);
  // Check if returning from challenge submission
  useEffect(() => {
    if (location.state?.challenged) {
      setHasBeenChallenged(true);
    }
  }, [location.state]);
  // Close note menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showNoteMenu) {
        const target = event.target as HTMLElement;
        if (!target.closest('.note-menu-container')) {
          setShowNoteMenu(null);
        }
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showNoteMenu]);
  const rowsPerPage = 5;
  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };
  const handleChallengeClick = () => {
    navigate(`/disputes/${id}/challenge`);
  };
  const handleViewChallengeDetails = () => {
    setShowChallengeDetails(true);
  };
  const handleChallengeConfirm = () => {
    setShowChallengeModal(false);
    setShowChallengeFlow(true);
    setChallengeStep('reason');
  };
  const handleChallengeReasonNext = () => {
    setChallengeStep('evidence');
  };
  const handleEvidenceNext = () => {
    setChallengeStep('order');
  };
  const handleOrderNext = () => {
    setChallengeStep('customer');
  };
  const handleCustomerNext = () => {
    setChallengeStep('other');
  };
  const handleOtherNext = () => {
    // Check if user has provided no evidence at all
    if (noEvidenceChecked && noOrderEvidenceChecked && noCustomerEvidenceChecked && noOtherEvidenceChecked) {
      setShowNoEvidenceWarning(true);
    } else {
      setChallengeStep('review');
    }
  };
  const handleReviewBack = () => {
    setChallengeStep('other');
  };
  const handleCloseChallengeFlow = () => {
    setShowChallengeFlow(false);
    setChallengeStep('reason');
    setSelectedChallengeReason('purchasing_history');
    setSelectedProductType('');
    setNoEvidenceChecked(false);
    setNoOrderEvidenceChecked(false);
    setNoCustomerEvidenceChecked(false);
    setNoOtherEvidenceChecked(false);
    setShowNoEvidenceWarning(false);
    setReviewConfirmChecked(false);
  };
  const handleAcceptClick = () => {
    setShowAcceptModal(true);
  };
  const handleAcceptConfirm = () => {
    setIsAccepted(true);
    setShowAcceptModal(false);
  };
  const handleAddNote = () => {
    if (noteText.trim()) {
      const newNote = {
        id: `note-${Date.now()}`,
        timestamp: new Date().toISOString(),
        description: noteText.trim(),
        status: 'note' as const,
        author: 'Eng Han Ng',
        isPinned: false
      };
      setMerchantNotes([newNote, ...merchantNotes]);
      setNoteText('');
      setShowAddNote(false);
    }
  };
  const handleEditNote = (noteId: string) => {
    const note = merchantNotes.find(n => n.id === noteId);
    if (note) {
      setEditingNoteId(noteId);
      setEditingNoteText(note.description);
      setShowNoteMenu(null);
    }
  };
  const handleSaveEdit = (noteId: string) => {
    if (editingNoteText.trim()) {
      setMerchantNotes(merchantNotes.map(note => note.id === noteId ? {
        ...note,
        description: editingNoteText.trim()
      } : note));
      setEditingNoteId(null);
      setEditingNoteText('');
    }
  };
  const handleCancelEdit = () => {
    setEditingNoteId(null);
    setEditingNoteText('');
  };
  const handleDeleteNote = (noteId: string) => {
    setMerchantNotes(merchantNotes.filter(note => note.id !== noteId));
    setShowNoteMenu(null);
  };
  const handlePinNote = (noteId: string) => {
    setMerchantNotes(merchantNotes.map(note => note.id === noteId ? {
      ...note,
      isPinned: !note.isPinned
    } : note));
    setShowNoteMenu(null);
  };
  const handleCopyOrderId = async (orderId: string) => {
    try {
      await navigator.clipboard.writeText(orderId);
      setCopiedOrderId(orderId);
      setTimeout(() => setCopiedOrderId(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };
  const getFileFormatRequirements = () => {
    const cardBrand = dispute.cardBrand.toLowerCase();
    // Check for Amex
    if (cardBrand === 'amex') {
      return {
        formats: 'Supported formats: TIFF, PDF, JPEG, JPG, PNG, DOC, Maximum individual file size: 4MB, Maximum PDF pages: less than 20 pages, Maximum image size: Not more than 300 x 300 DPI',
        language: 'Evidence language: English',
        files: 'Supported files: TIFF, TIF, PDF, JPEG, JPG, DOC, PNG and maximum combined file sizes: 25MB across all files.'
      };
    }
    // Check for Mastercard
    if (cardBrand === 'mastercard') {
      return {
        formats: 'Accepted file formats: TIFF, PDF, JPEG, PNG. File name restrictions: Use both English characters and numbers. Maximum document / image size: Not more than 300 x 300 DPI and pixel count should be less than 30 million',
        language: 'Evidence language: English',
        files: 'Supported files: TIFF, TIF, PDF, JPEG, JPG, PNG and maximum combined file sizes: 14.5MB across all files.'
      };
    }
    // Visa and other card brands (default)
    return {
      formats: 'Supported formats: TIFF, PDF, PNG, JPEG. Maximum individual file size: 2MB. Maximum page size for TIFF and JPEG: 8.64 inches x 14 inches',
      language: 'Evidence language: English',
      files: 'Supported files: TIFF, TIF, PDF, PNG, JPEG, JPG and maximum combined file sizes: 10MB across all files.'
    };
  };
  // Mock related orders data
  const relatedOrders: RelatedOrder[] = [{
    id: '1',
    orderId: '8b89fb9a-3fce-4aea-956f-49aa8d033caa',
    amount: 10000,
    currency: getTenant().currency.code,
    createdTime: '2026-04-15 09:01:43 UTC+0800',
    status: 'Completed',
    matchingCriteria: 6,
    totalCriteria: 7
  }, {
    id: '2',
    orderId: 'bcd5898f-5af4-4bb8-99ee-6f2a308d799b',
    amount: 10000,
    currency: getTenant().currency.code,
    createdTime: '2026-04-15 09:01:41 UTC+0800',
    status: 'Completed',
    matchingCriteria: 6,
    totalCriteria: 7
  }, {
    id: '3',
    orderId: '4086dc8d-fb0b-44d0-8e8c-cf018f0c778d',
    amount: 10000,
    currency: getTenant().currency.code,
    createdTime: '2026-04-15 08:49:46 UTC+0800',
    status: 'Completed',
    matchingCriteria: 6,
    totalCriteria: 7
  }, {
    id: '4',
    orderId: 'a5cf0edf-9412-4ffe-9275-b8914ba25e25',
    amount: 10000,
    currency: getTenant().currency.code,
    createdTime: '2026-04-15 08:49:33 UTC+0800',
    status: 'Completed',
    matchingCriteria: 6,
    totalCriteria: 7
  }, {
    id: '5',
    orderId: '6d92fede-b0c8-4efd-b633-230703873 56f',
    amount: 10000,
    currency: getTenant().currency.code,
    createdTime: '2026-04-15 08:49:31 UTC+0800',
    status: 'Completed',
    matchingCriteria: 7,
    totalCriteria: 7
  }];
  const matchingCriteriaDetails: MatchingCriteriaDetail[] = [{
    category: 'Customer information',
    subtitle: 'Must match at least two criteria',
    items: [{
      label: 'IP address',
      matched: true
    }, {
      label: 'Device ID / fingerprint',
      matched: true
    }, {
      label: 'Shipping address',
      matched: true
    }, {
      label: 'Email address',
      matched: false
    }]
  }, {
    category: 'Transaction information',
    subtitle: 'Must match at least two criteria',
    items: [{
      label: '120 - 365 days older',
      matched: true
    }, {
      label: 'Undisputed & not fraudulent',
      matched: true
    }]
  }, {
    category: 'Additional information',
    subtitle: '',
    items: [{
      label: 'Payment method',
      matched: true
    }]
  }];
  const totalPages = Math.ceil(relatedOrders.length / rowsPerPage);
  const paginatedOrders = relatedOrders.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage);
  // Find the dispute by ID using centralized data
  const allDisputes = useMemo(() => generateDisputes(), []);
  const dispute = useMemo(() => {
    const found = allDisputes.find(d => d.id === id);
    if (!found) {
      // Return first dispute as fallback
      return allDisputes[0];
    }
    return found;
  }, [id, allDisputes]);
  // Check if dispute is challenged - either from the dispute status or from navigation state
  const isChallenged = dispute.status === 'challenged' || location.state?.challenged === true || hasBeenChallenged;
  // Get effective status (use accepted if user just accepted it)
  const effectiveStatus = isAccepted ? 'accepted' : dispute.status;
  const paymentAmount = dispute.amount;
  const disputedAmount = dispute.amount;
  const disputeFee = Math.round(dispute.amount * 0.08);
  const netAmount = disputedAmount + disputeFee;
  const formatCurrency = (amount: number, currency: string = getTenant().currency.code) => {
    return formatCurrencyFromTokens(amount, {
      currency,
      fractionDigits: 2
    });
  };
  const getStatusBadge = (status: DisputeStatus) => {
    const styles = {
      requires_response: 'text-[#E2231A] bg-[#FEE7E5]',
      expired: 'text-[#374151] bg-[#E5E7EB]',
      challenged: 'text-[#1D4ED8] bg-[#DBEAFE]',
      accepted: 'text-[#C41B14] bg-[#FBD4D1]',
      reversed: 'text-[#166534] bg-[#DCFCE7]',
      pending_closure: 'text-[#4B5563] bg-[#E5E7EB]',
      pending_decision: 'text-[#1D4ED8] bg-[#DBEAFE]',
      won: 'text-[#166534] bg-[#DCFCE7]',
      lost: 'text-[#B91C1C] bg-[#FEE2E2]'
    };
    const labels = {
      requires_response: 'Requires response',
      expired: 'Expired',
      challenged: 'Challenged',
      accepted: 'Accepted',
      reversed: 'Reversed',
      pending_closure: 'Pending Closure',
      pending_decision: 'Pending Decision',
      won: 'Won',
      lost: 'Lost'
    };
    return <span className={`inline-flex items-center px-3 py-1 rounded-md text-sm font-medium ${styles[status]}`} data-id="element-5414">
        {labels[status]}
      </span>;
  };
  const getHoursRemaining = (dueDate: string) => {
    const now = new Date();
    const due = new Date(dueDate);
    const diffMs = due.getTime() - now.getTime();
    const diffHours = Math.ceil(diffMs / (1000 * 60 * 60));
    return diffHours;
  };
  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-GB', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  const getReasonDescription = (reason: DisputeReason): string => {
    const descriptions: Record<DisputeReason, string> = {
      duplicate: 'The customer claims this is a duplicate charge',
      fraudulent: 'The customer claims the transaction was fraudulent',
      subscription_canceled: 'The customer claims they canceled their subscription',
      product_unacceptable: 'The customer claims the product was unacceptable',
      product_not_received: 'The customer claims they did not receive the product',
      unrecognized: 'The customer does not recognize the transaction',
      credit_not_processed: 'The customer claims a credit was not processed',
      incorrect_account_details: 'The customer claims incorrect account details were used',
      insufficient_funds: 'The customer claims they had insufficient funds',
      bank_cannot_process: 'The bank cannot process the transaction',
      debit_not_authorized: 'The customer claims the debit was not authorized',
      general: 'General dispute reason',
      customer_initiated: 'Customer initiated the dispute',
      check_returned: 'The check was returned',
      noncompliant: 'The transaction was noncompliant'
    };
    return descriptions[reason] || 'Dispute reason not specified';
  };
  const getReasonLabel = (reason: DisputeReason): string => {
    const labels: Record<DisputeReason, string> = {
      duplicate: 'Duplicate',
      fraudulent: 'Fraudulent',
      subscription_canceled: 'Subscription canceled',
      product_unacceptable: 'Product unacceptable',
      product_not_received: 'Product not received',
      unrecognized: 'Unrecognized',
      credit_not_processed: 'Credit not processed',
      incorrect_account_details: 'Incorrect account details',
      insufficient_funds: 'Insufficient funds',
      bank_cannot_process: 'Bank cannot process',
      debit_not_authorized: 'Debit not authorized',
      general: 'General',
      customer_initiated: 'Customer initiated',
      check_returned: 'Check returned',
      noncompliant: 'Noncompliant'
    };
    return labels[reason];
  };
  const getPaymentSource = () => {
    // In a real app, this would come from the dispute data
    // For now, we'll use a simple logic based on dispute ID
    const sources = ['Payment Link', 'QR Code', 'Invoice', 'Tap to Phone', 'POS Terminal'];
    const sourceIcons = [LinkIcon, QrCodeIcon, FileIcon, SmartphoneIcon, TerminalIcon];
    const index = parseInt(dispute.id) % sources.length;
    return {
      name: sources[index],
      Icon: sourceIcons[index]
    };
  };
  const hoursRemaining = getHoursRemaining(dispute.dueDate);
  const reversedLifecycle = useMemo(() => {
    const lifecycle = [...dispute.lifecycle];
    if (isChallenged) {
      lifecycle.push({
        id: '3',
        timestamp: new Date().toISOString(),
        status: 'challenged' as DisputeStatus,
        description: 'Dispute challenged with supporting evidence'
      });
    }
    if (isAccepted) {
      lifecycle.push({
        id: String(lifecycle.length + 1),
        timestamp: new Date().toISOString(),
        status: 'accepted' as DisputeStatus,
        description: 'Dispute accepted by merchant'
      });
    }
    // Separate pinned and unpinned notes
    const pinnedNotes = merchantNotes.filter(note => note.isPinned);
    const unpinnedNotes = merchantNotes.filter(note => !note.isPinned);
    // Sort unpinned notes by timestamp
    const sortedUnpinnedNotes = unpinnedNotes.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
    // Combine: pinned notes first (sorted by timestamp), then lifecycle events and unpinned notes
    const allEvents = [...lifecycle, ...sortedUnpinnedNotes];
    const sortedEvents = allEvents.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
    // Add pinned notes at the top
    return [...pinnedNotes.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()), ...sortedEvents];
  }, [dispute.lifecycle, isChallenged, isAccepted, merchantNotes]);
  // Get evidence recommendations using the centralized utility
  const evidenceRecs = useMemo(() => {
    // LOGGING: Start of evidence recommendation lookup
    console.group('🔍 Evidence Recommendation Lookup');
    console.log('Dispute ID:', dispute.id);
    console.log('Card Brand (raw):', dispute.cardBrand);
    console.log('Network Reason Code (raw):', dispute.networkReasonCode);
    console.log('Dispute Reason:', dispute.reason);
    const result = getEvidenceRecommendation(dispute.cardBrand, dispute.networkReasonCode);
    console.log('Lookup Result:', {
      title: result.title,
      description: result.description,
      recommendationsCount: result.recommendations.length,
      recommendations: result.recommendations.map(r => r.title)
    });
    console.groupEnd();
    return result;
  }, [dispute.cardBrand, dispute.networkReasonCode]);
  // If challenge flow is active, show full-screen challenge interface
  if (showChallengeFlow) {
    return <div className="min-h-screen bg-white" data-id="element-5415">
        {/* Header */}
        <div className="border-b border-gray-200" data-id="element-5416">
          <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between" data-id="element-5417">
            <div className="flex items-center gap-3" data-id="element-5418">
              <div className="flex items-center gap-2" data-id="element-5419">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-5420">
                  <path d="M3 7L12 2L21 7L12 12L3 7Z" fill="#E2231A" stroke="#E2231A" strokeWidth="2" strokeLinejoin="round" data-id="element-5421" />

                  <path d="M3 12L12 17L21 12" stroke="#E2231A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-5422" />

                  <path d="M3 17L12 22L21 17" stroke="#E2231A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-5423" />
                </svg>
                <span className="text-lg font-semibold" data-id="element-5424">
                  {t('pages.DisputeDetail.pomelo_pay')}
                </span>
              </div>
              <span className="text-gray-400" data-id="element-5425">|</span>
              <span className="text-gray-700" data-id="element-5426">
                {t('pages.ChallengeDispute.challenge_dispute')}
              </span>
            </div>
            <button onClick={handleCloseChallengeFlow} className="text-gray-400 hover:text-gray-600" data-id="element-5427">
              <CloseIcon size={24} data-id="element-5428" />
            </button>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-6 py-8" data-id="element-5429">
          <div className="flex gap-8" data-id="element-5430">
            {/* Left Sidebar */}
            <div className="w-64 flex-shrink-0" data-id="element-5431">
              <div className="space-y-1" data-id="element-5432">
                <button className={`w-full text-left px-3 py-2 rounded text-sm flex items-center justify-between ${expandedSections.challengeDetails ? 'bg-gray-100' : ''}`} onClick={() => toggleSection('challengeDetails')} data-id="element-5433">
                  <span className="font-medium" data-id="element-5434">
                    {t('pages.ChallengeDispute.challenge_details')}
                  </span>
                  {expandedSections.challengeDetails ? <ChevronUpIcon size={16} data-id="element-5435" /> : <ChevronDownIcon size={16} data-id="element-5436" />}
                </button>
                {expandedSections.challengeDetails && <div className="ml-3 space-y-1" data-id="element-5437">
                    <button className={`w-full text-left px-3 py-2 rounded text-sm flex items-center gap-2 ${challengeStep === 'reason' ? 'text-blue-600 bg-blue-50' : 'text-gray-700'}`} data-id="element-5438">
                      {['evidence', 'order', 'customer', 'other', 'review'].includes(challengeStep) && <CheckCircleIcon size={16} className="text-green-600" data-id="element-5439" />}
                      {t('pages.ChallengeDispute.challenge_reason')}
                    </button>
                    <button className={`w-full text-left px-3 py-2 rounded text-sm flex items-center gap-2 ${challengeStep === 'evidence' ? 'text-blue-600 bg-blue-50' : challengeStep === 'reason' ? 'text-gray-500' : 'text-gray-700'}`} data-id="element-5440">
                      {['order', 'customer', 'other', 'review'].includes(challengeStep) && <CheckCircleIcon size={16} className="text-green-600" data-id="element-5441" />}
                      {t('pages.ChallengeDispute.supporting_evidence')}
                    </button>
                  </div>}

                <button className="w-full text-left px-3 py-2 rounded text-sm flex items-center justify-between text-gray-500" onClick={() => toggleSection('transactionDetails')} data-id="element-5442">
                  <span data-id="element-5443">{t('pages.ChallengeDispute.transaction_details')}</span>
                  {expandedSections.transactionDetails ? <ChevronUpIcon size={16} data-id="element-5444" /> : <ChevronDownIcon size={16} data-id="element-5445" />}
                </button>
                {expandedSections.transactionDetails && <div className="ml-3 space-y-1" data-id="element-5446">
                    <button className={`w-full text-left px-3 py-2 rounded text-sm flex items-center gap-2 ${challengeStep === 'order' ? 'text-blue-600 bg-blue-50' : ['reason', 'evidence'].includes(challengeStep) ? 'text-gray-500' : 'text-gray-700'}`} data-id="element-5447">
                      {['customer', 'other', 'review'].includes(challengeStep) && <CheckCircleIcon size={16} className="text-green-600" data-id="element-5448" />}
                      {t('pages.ChallengeDispute.order_details')}
                    </button>
                    <button className={`w-full text-left px-3 py-2 rounded text-sm flex items-center gap-2 ${challengeStep === 'customer' ? 'text-blue-600 bg-blue-50' : ['reason', 'evidence', 'order'].includes(challengeStep) ? 'text-gray-500' : 'text-gray-700'}`} data-id="element-5449">
                      {['other', 'review'].includes(challengeStep) && <CheckCircleIcon size={16} className="text-green-600" data-id="element-5450" />}
                      {t('pages.ChallengeDispute.customer_details')}
                    </button>
                  </div>}

                <button className="w-full text-left px-3 py-2 rounded text-sm flex items-center justify-between text-gray-500" onClick={() => toggleSection('otherEvidence')} data-id="element-5451">
                  <span data-id="element-5452">{t('pages.ChallengeDispute.other_evidence')}</span>
                  {expandedSections.otherEvidence ? <ChevronUpIcon size={16} data-id="element-5453" /> : <ChevronDownIcon size={16} data-id="element-5454" />}
                </button>
                {expandedSections.otherEvidence && <div className="ml-3 space-y-1" data-id="element-5455">
                    <button className={`w-full text-left px-3 py-2 rounded text-sm flex items-center gap-2 ${challengeStep === 'other' ? 'text-blue-600 bg-blue-50' : ['reason', 'evidence', 'order', 'customer'].includes(challengeStep) ? 'text-gray-500' : 'text-gray-700'}`} data-id="element-5456">
                      {challengeStep === 'review' && <CheckCircleIcon size={16} className="text-green-600" data-id="element-5457" />}
                      {t('pages.ChallengeDispute.other_supporting_documents')}
                    </button>
                  </div>}
              </div>
            </div>

            {/* Main Content */}
            <div className="flex-1" data-id="element-5458">
              {challengeStep === 'reason' && <div className="max-w-3xl" data-id="element-5459">
                  <div className="flex items-start justify-between mb-6" data-id="element-5460">
                    <div data-id="element-5461">
                      <h1 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-5462">
                        {t('pages.ChallengeDispute.challenge_reason')}
                      </h1>
                      <p className="text-gray-600" data-id="element-5463">
                        {t('pages.DisputeDetail.your_responses_will_help_us_recommend')}
                      </p>
                    </div>
                    <div className="text-right" data-id="element-5464">
                      <div className="text-sm text-gray-500 mb-1" data-id="element-5465">
                        {t('pages.ChallengeDispute.dispute_id')}{' '}
                        {dispute.disputeId}
                      </div>
                      <button className="text-sm text-blue-600 hover:text-blue-700" data-id="element-5466">
                        {t('pages.ChallengeDispute.view_details')}
                      </button>
                      <div className="mt-2 text-sm" data-id="element-5467">
                        <div className="text-gray-500" data-id="element-5468">
                          {t('pages.ChallengeDispute.dispute_amount')}
                        </div>
                        <div className="font-medium" data-id="element-5469">
                          {formatCurrency(dispute.amount)}
                        </div>
                      </div>
                      <div className="mt-2 text-sm" data-id="element-5470">
                        <div className="text-gray-500" data-id="element-5471">
                          {t('pages.ChallengeDispute.response_due')}
                        </div>
                        <div className="font-medium" data-id="element-5472">
                          {formatDateTime(dispute.dueDate)}
                        </div>
                        <div className="text-red-600 font-medium" data-id="element-5473">
                          {t('pages.ChallengeDispute.due_in_1_hour')}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-6" data-id="element-5474">
                    <div data-id="element-5475">
                      <label className="block text-sm font-medium text-gray-900 mb-3" data-id="element-5476">
                        {t('pages.ChallengeDispute.why_are_you_challenging_the_dispute')}
                      </label>
                      <p className="text-sm text-gray-600 mb-3" data-id="element-5477">
                        {t('pages.ChallengeDispute.choose_the_closest_option')}
                      </p>
                      <div className="space-y-3" data-id="element-5478">
                        <label className="flex items-start gap-3 p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50" data-id="element-5479">
                          <input type="radio" name="challengeReason" value="withdrew" checked={selectedChallengeReason === 'withdrew'} onChange={e => setSelectedChallengeReason(e.target.value)} className="mt-1" data-id="element-5480" />

                          <span className="text-sm text-gray-900" data-id="element-5481">
                            {t('pages.ChallengeDispute.the_customer_withdrew_the_dispute')}
                          </span>
                        </label>
                        <label className="flex items-start gap-3 p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50" data-id="element-5482">
                          <input type="radio" name="challengeReason" value="refunded" checked={selectedChallengeReason === 'refunded'} onChange={e => setSelectedChallengeReason(e.target.value)} className="mt-1" data-id="element-5483" />

                          <span className="text-sm text-gray-900" data-id="element-5484">
                            {t('pages.ChallengeDispute.the_customer_has_already_been_refunded')}
                          </span>
                        </label>
                        <label className="flex items-start gap-3 p-3 border-2 border-blue-600 bg-blue-50 rounded-lg cursor-pointer" data-id="element-5485">
                          <input type="radio" name="challengeReason" value="purchasing_history" checked={selectedChallengeReason === 'purchasing_history'} onChange={e => setSelectedChallengeReason(e.target.value)} className="mt-1" data-id="element-5486" />

                          <div className="flex-1" data-id="element-5487">
                            <div className="text-sm text-gray-900 font-medium" data-id="element-5488">
                              {t('pages.ChallengeDispute.the_customer_has_a_purchasing_history')}
                            </div>
                            {selectedChallengeReason === 'purchasing_history' && <div className="mt-3 p-3 bg-white rounded border border-blue-200" data-id="element-5489">
                                <p className="text-sm text-gray-700" data-id="element-5490">
                                  {t('pages.DisputeDetail.we_recommend_this_reason_as_we')}
                                </p>
                              </div>}
                          </div>
                        </label>
                        <label className="flex items-start gap-3 p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50" data-id="element-5491">
                          <input type="radio" name="challengeReason" value="other" checked={selectedChallengeReason === 'other'} onChange={e => setSelectedChallengeReason(e.target.value)} className="mt-1" data-id="element-5492" />

                          <span className="text-sm text-gray-900" data-id="element-5493">
                            {t('pages.ChallengeDispute.other_reasons')}
                          </span>
                        </label>
                      </div>
                    </div>

                    <div data-id="element-5494">
                      <label className="block text-sm font-medium text-gray-900 mb-3" data-id="element-5495">
                        {t('pages.DisputeDetail.what_type_of_productservice_was_the')}
                      </label>
                      <p className="text-sm text-gray-600 mb-3" data-id="element-5496">
                        {t('pages.ChallengeDispute.choose_the_closest_option')}
                      </p>
                      <div className="space-y-3" data-id="element-5497">
                        <label className="flex items-start gap-3 p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50" data-id="element-5498">
                          <input type="radio" name="productType" value="physical" checked={selectedProductType === 'physical'} onChange={e => setSelectedProductType(e.target.value)} className="mt-1" data-id="element-5499" />

                          <div data-id="element-5500">
                            <div className="text-sm text-gray-900 font-medium" data-id="element-5501">
                              {t('pages.ChallengeDispute.physical_product')}
                            </div>
                            <div className="text-xs text-gray-500 mt-1" data-id="element-5502">
                              {t('pages.DisputeDetail.physical_goods_to_be_delivered_to')}
                            </div>
                          </div>
                        </label>
                        <label className="flex items-start gap-3 p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50" data-id="element-5503">
                          <input type="radio" name="productType" value="digital" checked={selectedProductType === 'digital'} onChange={e => setSelectedProductType(e.target.value)} className="mt-1" data-id="element-5504" />

                          <div data-id="element-5505">
                            <div className="text-sm text-gray-900 font-medium" data-id="element-5506">
                              {t('pages.ChallengeDispute.digital_product_or_service')}
                            </div>
                            <div className="text-xs text-gray-500 mt-1" data-id="element-5507">
                              {t('pages.DisputeDetail.purchased_access_to_software_digital_files')}
                            </div>
                          </div>
                        </label>
                        <label className="flex items-start gap-3 p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50" data-id="element-5508">
                          <input type="radio" name="productType" value="offline" checked={selectedProductType === 'offline'} onChange={e => setSelectedProductType(e.target.value)} className="mt-1" data-id="element-5509" />

                          <div data-id="element-5510">
                            <div className="text-sm text-gray-900 font-medium" data-id="element-5511">
                              {t('pages.ChallengeDispute.offline_service')}
                            </div>
                            <div className="text-xs text-gray-500 mt-1" data-id="element-5512">
                              {t('pages.DisputeDetail.labour_or_assistant_services_provided_either')}
                            </div>
                          </div>
                        </label>
                        <label className="flex items-start gap-3 p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50" data-id="element-5513">
                          <input type="radio" name="productType" value="reservation" checked={selectedProductType === 'reservation'} onChange={e => setSelectedProductType(e.target.value)} className="mt-1" data-id="element-5514" />

                          <div data-id="element-5515">
                            <div className="text-sm text-gray-900 font-medium" data-id="element-5516">
                              {t('pages.ChallengeDispute.reservation_or_booking')}
                            </div>
                            <div className="text-xs text-gray-500 mt-1" data-id="element-5517">
                              {t('pages.DisputeDetail.services_related_to_reservations_or_bookings')}
                            </div>
                          </div>
                        </label>
                        <label className="flex items-start gap-3 p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50" data-id="element-5518">
                          <input type="radio" name="productType" value="other_product" checked={selectedProductType === 'other_product'} onChange={e => setSelectedProductType(e.target.value)} className="mt-1" data-id="element-5519" />

                          <span className="text-sm text-gray-900" data-id="element-5520">
                            {t('pages.ChallengeDispute.other')}
                          </span>
                        </label>
                      </div>
                    </div>
                  </div>

                  <div className="mt-8 flex items-center justify-between pt-6 border-t border-gray-200" data-id="element-5521">
                    <button onClick={handleCloseChallengeFlow} className="px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700" data-id="element-5522">
                      {t('pages.ChallengeDispute.cancel')}
                    </button>
                    <button onClick={handleChallengeReasonNext} disabled={!selectedProductType} className="px-6 py-2 text-sm font-medium text-white bg-gray-300 rounded-md hover:bg-gray-400 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-5523">
                      {t('pages.ChallengeDispute.next')}
                    </button>
                  </div>
                </div>}

              {challengeStep === 'evidence' && <div className="max-w-3xl" data-id="element-5524">
                  <div className="flex items-start justify-between mb-6" data-id="element-5525">
                    <div data-id="element-5526">
                      <h1 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-5527">
                        {evidenceRecs.title}
                      </h1>
                      <p className="text-gray-600" data-id="element-5528">
                        {evidenceRecs.description}
                        {evidenceRecs.learnMoreLink && <>
                            {' '}
                            <a href="#" className="text-blue-600 hover:text-blue-700" data-id="element-5529">
                              {evidenceRecs.learnMoreLink}
                            </a>
                          </>}
                      </p>
                    </div>
                    <div className="text-right" data-id="element-5530">
                      <div className="text-sm text-gray-500 mb-1" data-id="element-5531">
                        {t('pages.ChallengeDispute.dispute_id')}{' '}
                        {dispute.disputeId}
                      </div>
                      <button className="text-sm text-blue-600 hover:text-blue-700" data-id="element-5532">
                        {t('pages.ChallengeDispute.view_details')}
                      </button>
                      <div className="mt-2 text-sm" data-id="element-5533">
                        <div className="text-gray-500" data-id="element-5534">
                          {t('pages.ChallengeDispute.dispute_amount')}
                        </div>
                        <div className="font-medium" data-id="element-5535">
                          {formatCurrency(dispute.amount)}
                        </div>
                      </div>
                      <div className="mt-2 text-sm" data-id="element-5536">
                        <div className="text-gray-500" data-id="element-5537">
                          {t('pages.ChallengeDispute.response_due')}
                        </div>
                        <div className="font-medium" data-id="element-5538">
                          {formatDateTime(dispute.dueDate)}
                        </div>
                        <div className="text-red-600 font-medium" data-id="element-5539">
                          {t('pages.ChallengeDispute.due_in_1_hour')}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="mb-6" data-id="element-5540">
                    <h3 className="text-lg font-semibold text-gray-900 mb-3" data-id="element-5541">
                      {t('pages.ChallengeDispute.provide_supporting_evidence')}
                    </h3>
                    {/* LOGGING: Verify evidence is being rendered */}
                    {console.log('🎨 Rendering evidence step with:', {
                  title: evidenceRecs.title,
                  recommendationsCount: evidenceRecs.recommendations.length,
                  firstRecommendation: evidenceRecs.recommendations[0]
                })}
                    {dispute.networkReasonCode === '13.2' && <p className="text-sm text-gray-600 mb-4" data-id="element-5542">
                        {t('pages.DisputeDetail.for_visa_132_subscription_canceled_disputes')}
                      </p>}
                    {dispute.reason === 'subscription_canceled' && dispute.networkReasonCode !== '13.2' && <p className="text-sm text-gray-600 mb-4" data-id="element-5543">
                          {t('pages.DisputeDetail.for_subscription_cancellation_disputes_we_recommend')}
                        </p>}
                    {dispute.reason !== 'subscription_canceled' && <p className="text-sm text-gray-600 mb-4" data-id="element-5544">
                        {t('pages.ChallengeDispute.based_on_your_challenge_reason_we')}
                      </p>}
                    <ul className="list-disc pl-5 space-y-2 text-sm text-gray-700 mb-4" data-id="element-5545">
                      {evidenceRecs.recommendations.map((rec, index) => <li key={index} data-id="element-5546">
                          <strong data-id="element-5547">{rec.title}</strong>
                          {rec.description && `: ${rec.description}`}
                        </li>)}
                    </ul>
                    <p className="text-sm text-gray-500" data-id="element-5548">
                      {(() => {
                    const requirements = getFileFormatRequirements();
                    return <>
                            {requirements.formats}
                            <br data-id="element-5549" />
                            {requirements.language}
                            <br data-id="element-5550" />
                            {requirements.files}
                          </>;
                  })()}
                    </p>
                  </div>

                  <div className="mb-6" data-id="element-5551">
                    <button disabled={noEvidenceChecked} className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md border ${noEvidenceChecked ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed' : 'bg-white text-blue-600 border-blue-600 hover:bg-blue-50'}`} data-id="element-5552">
                      <UploadIcon size={16} data-id="element-5553" />
                      {t('pages.ChallengeDispute.upload_file')}
                    </button>
                  </div>

                  <div className="mb-6" data-id="element-5554">
                    <textarea placeholder={t('pages.ChallengeDispute.attrs.include_any_additional_file_descriptions_or')} rows={4} disabled={noEvidenceChecked} className={`w-full border rounded-md p-3 text-sm ${noEvidenceChecked ? 'bg-gray-50 text-gray-400 border-gray-200' : 'border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500'}`} data-id="element-5555" />
                  </div>

                  <div className="mb-6" data-id="element-5556">
                    <label className="flex items-start gap-3 cursor-pointer" data-id="element-5557">
                      <input type="checkbox" checked={noEvidenceChecked} onChange={e => setNoEvidenceChecked(e.target.checked)} className="mt-1 w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500" data-id="element-5558" />

                      <span className="text-sm text-gray-900" data-id="element-5559">
                        {t('pages.ChallengeDispute.i_do_not_have_any_evidence')}
                      </span>
                    </label>
                  </div>

                  <div className="mt-8 flex items-center justify-between pt-6 border-t border-gray-200" data-id="element-5560">
                    <button onClick={() => setChallengeStep('reason')} className="px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700" data-id="element-5561">
                      {t('pages.ChallengeDispute.back')}
                    </button>
                    <button onClick={handleEvidenceNext} className="px-6 py-2 text-sm font-medium text-white bg-gray-300 rounded-md hover:bg-gray-400" data-id="element-5562">
                      {t('pages.ChallengeDispute.next')}
                    </button>
                  </div>
                </div>}

              {challengeStep === 'order' && <div className="max-w-3xl" data-id="element-5563">
                  <div className="flex items-start justify-between mb-6" data-id="element-5564">
                    <div data-id="element-5565">
                      <h1 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-5566">
                        {t('pages.ChallengeDispute.order_details')}
                      </h1>
                      <p className="text-gray-600" data-id="element-5567">
                        {t('pages.DisputeDetail.please_provide_as_much_information_as')}
                      </p>
                      <p className="text-gray-600 mt-2" data-id="element-5568">
                        {t('pages.DisputeDetail.the_details_below_have_been_prefilled')}
                      </p>
                    </div>
                    <div className="text-right" data-id="element-5569">
                      <div className="text-sm text-gray-500 mb-1" data-id="element-5570">
                        {t('pages.ChallengeDispute.dispute_id')}{' '}
                        {dispute.disputeId}
                      </div>
                      <button className="text-sm text-blue-600 hover:text-blue-700" data-id="element-5571">
                        {t('pages.ChallengeDispute.view_details')}
                      </button>
                      <div className="mt-2 text-sm" data-id="element-5572">
                        <div className="text-gray-500" data-id="element-5573">
                          {t('pages.ChallengeDispute.dispute_amount')}
                        </div>
                        <div className="font-medium" data-id="element-5574">
                          {formatCurrency(dispute.amount)}
                        </div>
                      </div>
                      <div className="mt-2 text-sm" data-id="element-5575">
                        <div className="text-gray-500" data-id="element-5576">
                          {t('pages.ChallengeDispute.response_due')}
                        </div>
                        <div className="font-medium" data-id="element-5577">
                          {formatDateTime(dispute.dueDate)}
                        </div>
                        <div className="text-red-600 font-medium" data-id="element-5578">
                          {t('pages.ChallengeDispute.due_in_1_hour')}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-6" data-id="element-5579">
                    <div data-id="element-5580">
                      <label className="block text-sm font-medium text-gray-900 mb-2" data-id="element-5581">
                        {t('pages.ChallengeDispute.product_details')}
                        <button className="ml-2 text-blue-600 hover:text-blue-700" data-id="element-5582">
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-5583">
                            <path d="M8 12V8M8 4H8.01M15 8C15 11.866 11.866 15 8 15C4.13401 15 1 11.866 1 8C1 4.13401 4.13401 1 8 1C11.866 1 15 4.13401 15 8Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-5584" />
                          </svg>
                        </button>
                      </label>
                      <div className="flex items-center gap-2 text-sm text-gray-700" data-id="element-5585">
                        <span data-id="element-5586">
                          {t('pages.DisputeDetail.iphone7_5_10001_usd')}
                        </span>
                        <button className="text-blue-600 hover:text-blue-700" data-id="element-5587">
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-5588">
                            <path d="M11.3333 2.00004C11.5084 1.82494 11.7163 1.68605 11.9451 1.59129C12.1739 1.49653 12.4191 1.44775 12.6666 1.44775C12.9142 1.44775 13.1594 1.49653 13.3882 1.59129C13.617 1.68605 13.8249 1.82494 14 2.00004C14.1751 2.17513 14.314 2.383 14.4088 2.61178C14.5035 2.84055 14.5523 3.08575 14.5523 3.33337C14.5523 3.58099 14.5035 3.82619 14.4088 4.05497C14.314 4.28374 14.1751 4.49161 14 4.66671L5.00001 13.6667L1.33334 14.6667L2.33334 11L11.3333 2.00004Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-5589" />
                          </svg>
                        </button>
                      </div>
                    </div>

                    <div data-id="element-5590">
                      <label className="block text-sm font-medium text-gray-900 mb-2" data-id="element-5591">
                        {t('pages.ChallengeDispute.shipping_address')}
                        <button className="ml-2 text-blue-600 hover:text-blue-700" data-id="element-5592">
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-5593">
                            <path d="M8 12V8M8 4H8.01M15 8C15 11.866 11.866 15 8 15C4.13401 15 1 11.866 1 8C1 4.13401 4.13401 1 8 1C11.866 1 15 4.13401 15 8Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-5594" />
                          </svg>
                        </button>
                      </label>
                      <div className="flex items-center gap-2 text-sm text-gray-700" data-id="element-5595">
                        <span data-id="element-5596">
                          {t('pages.DisputeDetail.pomelo_pay_office_1_raffles_place')}
                        </span>
                        <button className="text-blue-600 hover:text-blue-700" data-id="element-5597">
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-5598">
                            <path d="M11.3333 2.00004C11.5084 1.82494 11.7163 1.68605 11.9451 1.59129C12.1739 1.49653 12.4191 1.44775 12.6666 1.44775C12.9142 1.44775 13.1594 1.49653 13.3882 1.59129C13.617 1.68605 13.8249 1.82494 14 2.00004C14.1751 2.17513 14.314 2.383 14.4088 2.61178C14.5035 2.84055 14.5523 3.08575 14.5523 3.33337C14.5523 3.58099 14.5035 3.82619 14.4088 4.05497C14.314 4.28374 14.1751 4.49161 14 4.66671L5.00001 13.6667L1.33334 14.6667L2.33334 11L11.3333 2.00004Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-5599" />
                          </svg>
                        </button>
                      </div>
                    </div>

                    <div data-id="element-5600">
                      <label className="block text-sm font-medium text-gray-900 mb-2" data-id="element-5601">
                        {t('pages.ChallengeDispute.shipping_carrier')}
                      </label>
                      <div className="flex items-center gap-2 text-sm text-gray-700" data-id="element-5602">
                        <span data-id="element-5603">{t('pages.ChallengeDispute.sameday')}</span>
                        <button className="text-blue-600 hover:text-blue-700" data-id="element-5604">
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-5605">
                            <path d="M11.3333 2.00004C11.5084 1.82494 11.7163 1.68605 11.9451 1.59129C12.1739 1.49653 12.4191 1.44775 12.6666 1.44775C12.9142 1.44775 13.1594 1.49653 13.3882 1.59129C13.617 1.68605 13.8249 1.82494 14 2.00004C14.1751 2.17513 14.314 2.383 14.4088 2.61178C14.5035 2.84055 14.5523 3.08575 14.5523 3.33337C14.5523 3.58099 14.5035 3.82619 14.4088 4.05497C14.314 4.28374 14.1751 4.49161 14 4.66671L5.00001 13.6667L1.33334 14.6667L2.33334 11L11.3333 2.00004Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-5606" />
                          </svg>
                        </button>
                      </div>
                    </div>

                    <div data-id="element-5607">
                      <h3 className="text-lg font-semibold text-gray-900 mb-3" data-id="element-5608">
                        {t('pages.ChallengeDispute.provide_additional_supporting_evidence')}
                      </h3>
                      <p className="text-sm text-gray-600 mb-4" data-id="element-5609">
                        {t('pages.ChallengeDispute.as_the_payment_was_for_a')}{' '}
                        <span className="font-medium" data-id="element-5610">
                          {t('pages.ChallengeDispute.physical_product')}
                        </span>
                        {t('pages.DisputeDetail.we_recommend_you_to_also_provide')}
                      </p>
                      <ul className="list-disc pl-5 space-y-1 text-sm text-gray-700 mb-4" data-id="element-5611">
                        <li data-id="element-5612">{t('pages.ChallengeDispute.shipping_date')}</li>
                        <li data-id="element-5613">
                          {t('pages.ChallengeDispute.shipping_tracking_number')}
                        </li>
                      </ul>
                      <p className="text-sm text-gray-600 mb-4" data-id="element-5614">
                        {t('pages.DisputeDetail.you_can_upload_any_document_containing')}
                      </p>
                      <p className="text-sm text-gray-500 mb-4" data-id="element-5615">
                        {(() => {
                      const requirements = getFileFormatRequirements();
                      return <>
                              {requirements.formats}
                              <br data-id="element-5616" />
                              {requirements.language}
                              <br data-id="element-5617" />
                              {requirements.files}
                            </>;
                    })()}
                      </p>
                    </div>

                    <div data-id="element-5618">
                      <button disabled={noOrderEvidenceChecked} className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md border ${noOrderEvidenceChecked ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed' : 'bg-white text-blue-600 border-blue-600 hover:bg-blue-50'}`} data-id="element-5619">
                        <UploadIcon size={16} data-id="element-5620" />
                        {t('pages.ChallengeDispute.upload_file')}
                      </button>
                    </div>

                    <div data-id="element-5621">
                      <textarea placeholder={t('pages.ChallengeDispute.attrs.include_any_additional_file_descriptions_or')} rows={4} disabled={noOrderEvidenceChecked} className={`w-full border rounded-md p-3 text-sm ${noOrderEvidenceChecked ? 'bg-gray-50 text-gray-400 border-gray-200' : 'border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500'}`} data-id="element-5622" />
                    </div>

                    <div data-id="element-5623">
                      <label className="flex items-start gap-3 cursor-pointer" data-id="element-5624">
                        <input type="checkbox" checked={noOrderEvidenceChecked} onChange={e => setNoOrderEvidenceChecked(e.target.checked)} className="mt-1 w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500" data-id="element-5625" />

                        <span className="text-sm text-gray-900" data-id="element-5626">
                          {t('pages.ChallengeDispute.i_do_not_have_any_evidence')}
                        </span>
                      </label>
                    </div>
                  </div>

                  <div className="mt-8 flex items-center justify-between pt-6 border-t border-gray-200" data-id="element-5627">
                    <button onClick={() => setChallengeStep('evidence')} className="px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700" data-id="element-5628">
                      {t('pages.ChallengeDispute.back')}
                    </button>
                    <button onClick={handleOrderNext} className="px-6 py-2 text-sm font-medium text-white bg-gray-300 rounded-md hover:bg-gray-400" data-id="element-5629">
                      {t('pages.ChallengeDispute.next')}
                    </button>
                  </div>
                </div>}

              {challengeStep === 'customer' && <div className="max-w-3xl" data-id="element-5630">
                  <div className="flex items-start justify-between mb-6" data-id="element-5631">
                    <div data-id="element-5632">
                      <h1 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-5633">
                        {t('pages.ChallengeDispute.customer_details')}
                      </h1>
                      <p className="text-gray-600" data-id="element-5634">
                        {t('pages.DisputeDetail.please_provide_as_much_information_as_2')}
                      </p>
                      <p className="text-gray-600 mt-2" data-id="element-5635">
                        {t('pages.DisputeDetail.the_details_below_have_been_prefilled')}
                      </p>
                    </div>
                    <div className="text-right" data-id="element-5636">
                      <div className="text-sm text-gray-500 mb-1" data-id="element-5637">
                        {t('pages.ChallengeDispute.dispute_id')}{' '}
                        {dispute.disputeId}
                      </div>
                      <button className="text-sm text-blue-600 hover:text-blue-700" data-id="element-5638">
                        {t('pages.ChallengeDispute.view_details')}
                      </button>
                      <div className="mt-2 text-sm" data-id="element-5639">
                        <div className="text-gray-500" data-id="element-5640">
                          {t('pages.ChallengeDispute.dispute_amount')}
                        </div>
                        <div className="font-medium" data-id="element-5641">
                          {formatCurrency(dispute.amount)}
                        </div>
                      </div>
                      <div className="mt-2 text-sm" data-id="element-5642">
                        <div className="text-gray-500" data-id="element-5643">
                          {t('pages.ChallengeDispute.response_due')}
                        </div>
                        <div className="font-medium" data-id="element-5644">
                          {formatDateTime(dispute.dueDate)}
                        </div>
                        <div className="text-red-600 font-medium" data-id="element-5645">
                          {t('pages.ChallengeDispute.due_in_1_hour')}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-6" data-id="element-5646">
                    <div data-id="element-5647">
                      <label className="block text-sm font-medium text-gray-900 mb-2" data-id="element-5648">
                        {t('pages.ChallengeDispute.customer_name')}
                      </label>
                      <div className="flex items-center gap-2 text-sm text-gray-700" data-id="element-5649">
                        <span data-id="element-5650">{t('pages.ChallengeDispute.test_name')}</span>
                        <button className="text-blue-600 hover:text-blue-700" data-id="element-5651">
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-5652">
                            <path d="M11.3333 2.00004C11.5084 1.82494 11.7163 1.68605 11.9451 1.59129C12.1739 1.49653 12.4191 1.44775 12.6666 1.44775C12.9142 1.44775 13.1594 1.49653 13.3882 1.59129C13.617 1.68605 13.8249 1.82494 14 2.00004C14.1751 2.17513 14.314 2.383 14.4088 2.61178C14.5035 2.84055 14.5523 3.08575 14.5523 3.33337C14.5523 3.58099 14.5035 3.82619 14.4088 4.05497C14.314 4.28374 14.1751 4.49161 14 4.66671L5.00001 13.6667L1.33334 14.6667L2.33334 11L11.3333 2.00004Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-5653" />
                          </svg>
                        </button>
                      </div>
                    </div>

                    <div data-id="element-5654">
                      <label className="block text-sm font-medium text-gray-900 mb-2" data-id="element-5655">
                        {t('pages.ChallengeDispute.customer_email')}
                      </label>
                      <div className="flex items-center gap-2 text-sm text-gray-700" data-id="element-5656">
                        <span data-id="element-5657">
                          {t('pages.DisputeDetail.jessicapomelopaycom')}
                        </span>
                        <button className="text-blue-600 hover:text-blue-700" data-id="element-5658">
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-5659">
                            <path d="M11.3333 2.00004C11.5084 1.82494 11.7163 1.68605 11.9451 1.59129C12.1739 1.49653 12.4191 1.44775 12.6666 1.44775C12.9142 1.44775 13.1594 1.49653 13.3882 1.59129C13.617 1.68605 13.8249 1.82494 14 2.00004C14.1751 2.17513 14.314 2.383 14.4088 2.61178C14.5035 2.84055 14.5523 3.08575 14.5523 3.33337C14.5523 3.58099 14.5035 3.82619 14.4088 4.05497C14.314 4.28374 14.1751 4.49161 14 4.66671L5.00001 13.6667L1.33334 14.6667L2.33334 11L11.3333 2.00004Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-5660" />
                          </svg>
                        </button>
                      </div>
                    </div>

                    <div data-id="element-5661">
                      <label className="block text-sm font-medium text-gray-900 mb-2" data-id="element-5662">
                        {t('pages.DisputeDetail.customer_purchase_ip')}
                      </label>
                      <div className="flex items-center gap-2 text-sm text-gray-700" data-id="element-5663">
                        <span data-id="element-5664">35.221.170.121</span>
                        <button className="text-blue-600 hover:text-blue-700" data-id="element-5665">
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-5666">
                            <path d="M11.3333 2.00004C11.5084 1.82494 11.7163 1.68605 11.9451 1.59129C12.1739 1.49653 12.4191 1.44775 12.6666 1.44775C12.9142 1.44775 13.1594 1.49653 13.3882 1.59129C13.617 1.68605 13.8249 1.82494 14 2.00004C14.1751 2.17513 14.314 2.383 14.4088 2.61178C14.5035 2.84055 14.5523 3.08575 14.5523 3.33337C14.5523 3.58099 14.5035 3.82619 14.4088 4.05497C14.314 4.28374 14.1751 4.49161 14 4.66671L5.00001 13.6667L1.33334 14.6667L2.33334 11L11.3333 2.00004Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-5667" />
                          </svg>
                        </button>
                      </div>
                    </div>

                    <div data-id="element-5668">
                      <label className="block text-sm font-medium text-gray-900 mb-2" data-id="element-5669">
                        {t('pages.DisputeDetail.customer_phone')}
                      </label>
                      <div className="flex items-center gap-2 text-sm text-gray-700" data-id="element-5670">
                        <span data-id="element-5671">+65 9123 4567</span>
                        <button className="text-blue-600 hover:text-blue-700" data-id="element-5672">
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-5673">
                            <path d="M11.3333 2.00004C11.5084 1.82494 11.7163 1.68605 11.9451 1.59129C12.1739 1.49653 12.4191 1.44775 12.6666 1.44775C12.9142 1.44775 13.1594 1.49653 13.3882 1.59129C13.617 1.68605 13.8249 1.82494 14 2.00004C14.1751 2.17513 14.314 2.383 14.4088 2.61178C14.5035 2.84055 14.5523 3.08575 14.5523 3.33337C14.5523 3.58099 14.5035 3.82619 14.4088 4.05497C14.314 4.28374 14.1751 4.49161 14 4.66671L5.00001 13.6667L1.33334 14.6667L2.33334 11L11.3333 2.00004Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-5674" />
                          </svg>
                        </button>
                      </div>
                    </div>

                    <div data-id="element-5675">
                      <label className="block text-sm font-medium text-gray-900 mb-2" data-id="element-5676">
                        {t('pages.ChallengeDispute.customer_billing_address')}
                      </label>
                      <div className="flex items-center gap-2 text-sm text-gray-700" data-id="element-5677">
                        <span data-id="element-5678">
                          {t('pages.DisputeDetail.pomelo_pay_office_1_raffles_place')}
                        </span>
                        <button className="text-blue-600 hover:text-blue-700" data-id="element-5679">
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-5680">
                            <path d="M11.3333 2.00004C11.5084 1.82494 11.7163 1.68605 11.9451 1.59129C12.1739 1.49653 12.4191 1.44775 12.6666 1.44775C12.9142 1.44775 13.1594 1.49653 13.3882 1.59129C13.617 1.68605 13.8249 1.82494 14 2.00004C14.1751 2.17513 14.314 2.383 14.4088 2.61178C14.5035 2.84055 14.5523 3.08575 14.5523 3.33337C14.5523 3.58099 14.5035 3.82619 14.4088 4.05497C14.314 4.28374 14.1751 4.49161 14 4.66671L5.00001 13.6667L1.33334 14.6667L2.33334 11L11.3333 2.00004Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-5681" />
                          </svg>
                        </button>
                      </div>
                    </div>

                    <div data-id="element-5682">
                      <h3 className="text-lg font-semibold text-gray-900 mb-3" data-id="element-5683">
                        {t('pages.ChallengeDispute.provide_supporting_evidence')}
                      </h3>
                      <p className="text-sm text-gray-600 mb-4" data-id="element-5684">
                        {t('pages.DisputeDetail.we_recommend_you_to_provide_the')}
                      </p>
                      <p className="text-sm text-gray-600 mb-4" data-id="element-5685">
                        {t('pages.DisputeDetail.you_can_upload_any_document_containing_2')}
                      </p>
                      <p className="text-sm text-gray-500 mb-4" data-id="element-5686">
                        {(() => {
                      const requirements = getFileFormatRequirements();
                      return <>
                              {requirements.formats}
                              <br data-id="element-5687" />
                              {requirements.language}
                              <br data-id="element-5688" />
                              {requirements.files}
                            </>;
                    })()}
                      </p>
                    </div>

                    <div data-id="element-5689">
                      <button disabled={noCustomerEvidenceChecked} className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md border ${noCustomerEvidenceChecked ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed' : 'bg-white text-blue-600 border-blue-600 hover:bg-blue-50'}`} data-id="element-5690">
                        <UploadIcon size={16} data-id="element-5691" />
                        {t('pages.ChallengeDispute.upload_file')}
                      </button>
                    </div>

                    <div data-id="element-5692">
                      <textarea placeholder={t('pages.ChallengeDispute.attrs.include_any_additional_file_descriptions_or')} rows={4} disabled={noCustomerEvidenceChecked} className={`w-full border rounded-md p-3 text-sm ${noCustomerEvidenceChecked ? 'bg-gray-50 text-gray-400 border-gray-200' : 'border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500'}`} data-id="element-5693" />
                    </div>

                    <div data-id="element-5694">
                      <label className="flex items-start gap-3 cursor-pointer" data-id="element-5695">
                        <input type="checkbox" checked={noCustomerEvidenceChecked} onChange={e => setNoCustomerEvidenceChecked(e.target.checked)} className="mt-1 w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500" data-id="element-5696" />

                        <span className="text-sm text-gray-900" data-id="element-5697">
                          {t('pages.ChallengeDispute.i_do_not_have_any_evidence')}
                        </span>
                      </label>
                    </div>
                  </div>

                  <div className="mt-8 flex items-center justify-between pt-6 border-t border-gray-200" data-id="element-5698">
                    <button onClick={() => setChallengeStep('order')} className="px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700" data-id="element-5699">
                      {t('pages.ChallengeDispute.back')}
                    </button>
                    <button onClick={handleCustomerNext} className="px-6 py-2 text-sm font-medium text-white bg-gray-300 rounded-md hover:bg-gray-400" data-id="element-5700">
                      {t('pages.ChallengeDispute.next')}
                    </button>
                  </div>
                </div>}

              {challengeStep === 'other' && <div className="max-w-3xl" data-id="element-5701">
                  <div className="flex items-start justify-between mb-6" data-id="element-5702">
                    <div data-id="element-5703">
                      <h1 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-5704">
                        {t('pages.ChallengeDispute.other_supporting_documents')}
                      </h1>
                      <p className="text-gray-600" data-id="element-5705">
                        {t('pages.DisputeDetail.if_you_have_any_other_documents')}
                      </p>
                    </div>
                    <div className="text-right" data-id="element-5706">
                      <div className="text-sm text-gray-500 mb-1" data-id="element-5707">
                        {t('pages.ChallengeDispute.dispute_id')}{' '}
                        {dispute.disputeId}
                      </div>
                      <button className="text-sm text-blue-600 hover:text-blue-700" data-id="element-5708">
                        {t('pages.ChallengeDispute.view_details')}
                      </button>
                      <div className="mt-2 text-sm" data-id="element-5709">
                        <div className="text-gray-500" data-id="element-5710">
                          {t('pages.ChallengeDispute.dispute_amount')}
                        </div>
                        <div className="font-medium" data-id="element-5711">
                          {formatCurrency(dispute.amount)}
                        </div>
                      </div>
                      <div className="mt-2 text-sm" data-id="element-5712">
                        <div className="text-gray-500" data-id="element-5713">
                          {t('pages.ChallengeDispute.response_due')}
                        </div>
                        <div className="font-medium" data-id="element-5714">
                          {formatDateTime(dispute.dueDate)}
                        </div>
                        <div className="text-red-600 font-medium" data-id="element-5715">
                          {t('pages.ChallengeDispute.due_in_1_hour')}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-6" data-id="element-5716">
                    <div data-id="element-5717">
                      <h3 className="text-lg font-semibold text-gray-900 mb-3" data-id="element-5718">
                        {t('pages.ChallengeDispute.provide_supporting_evidence')}
                      </h3>
                      <p className="text-sm text-gray-500 mb-4" data-id="element-5719">
                        {(() => {
                      const requirements = getFileFormatRequirements();
                      return <>
                              {requirements.formats}
                              <br data-id="element-5720" />
                              {requirements.language}
                              <br data-id="element-5721" />
                              {requirements.files}
                            </>;
                    })()}
                      </p>
                    </div>

                    <div data-id="element-5722">
                      <button disabled={noOtherEvidenceChecked} className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md border ${noOtherEvidenceChecked ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed' : 'bg-white text-blue-600 border-blue-600 hover:bg-blue-50'}`} data-id="element-5723">
                        <UploadIcon size={16} data-id="element-5724" />
                        {t('pages.ChallengeDispute.upload_file')}
                      </button>
                    </div>

                    <div data-id="element-5725">
                      <textarea placeholder={t('pages.ChallengeDispute.attrs.include_any_additional_file_descriptions_or')} rows={4} disabled={noOtherEvidenceChecked} className={`w-full border rounded-md p-3 text-sm ${noOtherEvidenceChecked ? 'bg-gray-50 text-gray-400 border-gray-200' : 'border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500'}`} data-id="element-5726" />
                    </div>

                    <div data-id="element-5727">
                      <label className="flex items-start gap-3 cursor-pointer" data-id="element-5728">
                        <input type="checkbox" checked={noOtherEvidenceChecked} onChange={e => setNoOtherEvidenceChecked(e.target.checked)} className="mt-1 w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500" data-id="element-5729" />

                        <span className="text-sm text-gray-900" data-id="element-5730">
                          {t('pages.ChallengeDispute.i_do_not_have_any_evidence')}
                        </span>
                      </label>
                    </div>
                  </div>

                  <div className="mt-8 flex items-center justify-between pt-6 border-t border-gray-200" data-id="element-5731">
                    <button onClick={() => setChallengeStep('customer')} className="px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700" data-id="element-5732">
                      {t('pages.ChallengeDispute.back')}
                    </button>
                    <button onClick={handleOtherNext} className="px-6 py-2 text-sm font-medium text-white bg-gray-300 rounded-md hover:bg-gray-400" data-id="element-5733">
                      {t('pages.ChallengeDispute.review')}
                    </button>
                  </div>
                </div>}

              {challengeStep === 'review' && <div className="max-w-3xl" data-id="element-5734">
                  <div className="flex items-start justify-between mb-6" data-id="element-5735">
                    <div data-id="element-5736">
                      <h1 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-5737">
                        {t('pages.ChallengeDispute.review_your_challenge')}
                      </h1>
                      <p className="text-gray-600" data-id="element-5738">
                        {t('pages.DisputeDetail.please_confirm_that_the_information_you')}
                      </p>
                    </div>
                    <div className="text-right" data-id="element-5739">
                      <div className="text-sm text-gray-500 mb-1" data-id="element-5740">
                        {t('pages.ChallengeDispute.dispute_id')}{' '}
                        {dispute.disputeId}
                      </div>
                      <button className="text-sm text-blue-600 hover:text-blue-700" data-id="element-5741">
                        {t('pages.ChallengeDispute.view_details')}
                      </button>
                      <div className="mt-2 text-sm" data-id="element-5742">
                        <div className="text-gray-500" data-id="element-5743">
                          {t('pages.ChallengeDispute.dispute_amount')}
                        </div>
                        <div className="font-medium" data-id="element-5744">
                          {formatCurrency(dispute.amount)}
                        </div>
                      </div>
                      <div className="mt-2 text-sm" data-id="element-5745">
                        <div className="text-gray-500" data-id="element-5746">
                          {t('pages.ChallengeDispute.response_due')}
                        </div>
                        <div className="font-medium" data-id="element-5747">
                          {formatDateTime(dispute.dueDate)}
                        </div>
                        <div className="text-red-600 font-medium" data-id="element-5748">
                          {t('pages.ChallengeDispute.due_in_1_hour')}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-6" data-id="element-5749">
                    {/* Challenge details section */}
                    <div className="bg-gray-50 rounded-lg p-6" data-id="element-5750">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-5751">
                        {t('pages.ChallengeDispute.challenge_details')}
                      </h3>
                      <div className="space-y-3" data-id="element-5752">
                        <div className="flex justify-between" data-id="element-5753">
                          <span className="text-sm text-gray-600" data-id="element-5754">
                            {t('pages.ChallengeDispute.reason_for_challenge')}
                          </span>
                          <span className="text-sm text-gray-900" data-id="element-5755">
                            {t('pages.ChallengeDispute.the_customer_has_a_purchasing_history')}
                          </span>
                        </div>
                        <div className="flex justify-between" data-id="element-5756">
                          <span className="text-sm text-gray-600" data-id="element-5757">
                            {t('pages.ChallengeDispute.type_of_product_or_service')}
                          </span>
                          <span className="text-sm text-gray-900" data-id="element-5758">
                            {t('pages.ChallengeDispute.physical_product')}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Transaction details section */}
                    <div className="bg-gray-50 rounded-lg p-6" data-id="element-5759">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-5760">
                        {t('pages.ChallengeDispute.transaction_details')}
                      </h3>
                      <div className="space-y-3" data-id="element-5761">
                        <div className="flex" data-id="element-5762">
                          <span className="text-sm text-gray-600 w-48 flex-shrink-0" data-id="element-5763">
                            {t('pages.ChallengeDispute.product_details')}
                          </span>
                          <span className="text-sm text-gray-900" data-id="element-5764">
                            {t('pages.ChallengeDispute.iphone7_5')}{' '}
                            {formatCurrency(dispute.amount)}
                          </span>
                        </div>
                        <div className="flex" data-id="element-5765">
                          <span className="text-sm text-gray-600 w-48 flex-shrink-0" data-id="element-5766">
                            {t('pages.ChallengeDispute.shipping_address')}
                          </span>
                          <span className="text-sm text-gray-900" data-id="element-5767">
                            {t('pages.DisputeDetail.pomelo_pay_office_1_raffles_place')}
                          </span>
                        </div>
                        <div className="flex" data-id="element-5768">
                          <span className="text-sm text-gray-600 w-48 flex-shrink-0" data-id="element-5769">
                            {t('pages.ChallengeDispute.customer_name')}
                          </span>
                          <span className="text-sm text-gray-900" data-id="element-5770">
                            {t('pages.ChallengeDispute.test_name')}
                          </span>
                        </div>
                        <div className="flex" data-id="element-5771">
                          <span className="text-sm text-gray-600 w-48 flex-shrink-0" data-id="element-5772">
                            {t('pages.ChallengeDispute.customer_email')}
                          </span>
                          <span className="text-sm text-gray-900" data-id="element-5773">
                            {t('pages.DisputeDetail.customerpomelopaycom')}
                          </span>
                        </div>
                        <div className="flex" data-id="element-5774">
                          <span className="text-sm text-gray-600 w-48 flex-shrink-0" data-id="element-5775">
                            {t('pages.ChallengeDispute.customer_purchase_ip')}
                          </span>
                          <span className="text-sm text-gray-900" data-id="element-5776">
                            35.221.170.121
                          </span>
                        </div>
                        <div className="flex" data-id="element-5777">
                          <span className="text-sm text-gray-600 w-48 flex-shrink-0" data-id="element-5778">
                            {t('pages.ChallengeDispute.customer_billing_address')}
                          </span>
                          <span className="text-sm text-gray-900" data-id="element-5779">
                            {t('pages.DisputeDetail.pomelo_pay_office_1_raffles_place')}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Warning if no evidence */}
                    {(noEvidenceChecked || noOrderEvidenceChecked || noCustomerEvidenceChecked || noOtherEvidenceChecked) && <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3" data-id="element-5780">
                        <AlertTriangleIcon size={20} className="text-red-600 flex-shrink-0 mt-0.5" data-id="element-5781" />

                        <p className="text-sm text-gray-900" data-id="element-5782">
                          {t('pages.DisputeDetail.please_attach_at_least_one_of')}
                        </p>
                      </div>}

                    {/* Confirmation checkbox */}
                    <div data-id="element-5783">
                      <label className="flex items-start gap-3 cursor-pointer" data-id="element-5784">
                        <input type="checkbox" checked={reviewConfirmChecked} onChange={e => setReviewConfirmChecked(e.target.checked)} className="mt-1 w-5 h-5 text-[#C41B14] border-[#FBD4D1] rounded focus:ring-[#E2231A]" data-id="element-5785" />

                        <span className="text-sm text-gray-900" data-id="element-5786">
                          {t('pages.DisputeDetail.i_understand_that_the_current_evidence')}
                        </span>
                      </label>
                    </div>
                  </div>

                  <div className="mt-8 flex items-center justify-between pt-6 border-t border-gray-200" data-id="element-5787">
                    <button onClick={handleReviewBack} className="px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700" data-id="element-5788">
                      {t('pages.ChallengeDispute.back')}
                    </button>
                    <button disabled={!reviewConfirmChecked} className="px-6 py-2 text-sm font-medium text-white bg-[#E2231A] rounded-md hover:bg-[#C41B14] disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-5789">
                      {t('pages.ChallengeDispute.submit')}
                    </button>
                  </div>
                </div>}
            </div>
          </div>
        </div>

        {/* No Evidence Warning Modal */}
        {showNoEvidenceWarning && <div className="fixed inset-0 z-50 overflow-y-auto" data-id="element-5790">
            <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0" data-id="element-5791">
              <div className="fixed inset-0 transition-opacity" onClick={() => setShowNoEvidenceWarning(false)} data-id="element-5792">
                <div className="absolute inset-0 bg-gray-500 opacity-75" data-id="element-5793"></div>
              </div>

              <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true" data-id="element-5794">
                &#8203;
              </span>

              <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full" data-id="element-5795">
                <div className="bg-white px-6 pt-6 pb-4" data-id="element-5796">
                  <div className="flex items-start gap-4" data-id="element-5797">
                    <div className="flex-shrink-0" data-id="element-5798">
                      <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center" data-id="element-5799">
                        <AlertTriangleIcon size={24} className="text-red-600" data-id="element-5800" />
                      </div>
                    </div>
                    <div className="flex-1" data-id="element-5801">
                      <h3 className="text-lg font-semibold text-gray-900 mb-2" data-id="element-5802">
                        {t('pages.ChallengeDispute.are_you_sure_you_want_to')}
                      </h3>
                      <p className="text-sm text-gray-600" data-id="element-5803">
                        {t('pages.DisputeDetail.submitting_a_challenge_without_supporting_evidence')}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 px-6 py-4 flex items-center justify-end gap-3" data-id="element-5804">
                  <button onClick={() => setShowNoEvidenceWarning(false)} className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900" data-id="element-5805">
                    {t('pages.ChallengeDispute.go_back_and_add_evidence')}
                  </button>
                  <button onClick={() => {
                setShowNoEvidenceWarning(false);
                setChallengeStep('review');
              }} className="px-6 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700" data-id="element-5806">
                    {t('pages.ChallengeDispute.proceed_anyway')}
                  </button>
                </div>
              </div>
            </div>
          </div>}
      </div>;
  }
  return <div className="space-y-6" data-id="element-5807">
      {/* Header */}
      <div className="flex items-center justify-between" data-id="element-5808">
        <button onClick={() => navigate('/disputes')} className="flex items-center gap-2 text-gray-600 hover:text-gray-900" data-id="element-5809">
          <ArrowLeftIcon size={20} data-id="element-5810" />
          {t('pages.DisputeDetail.back_to_disputes')}
        </button>
      </div>

      {/* Dispute Header */}
      <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-5811">
        <div className="flex items-start justify-between mb-6" data-id="element-5812">
          <div className="flex items-center gap-3" data-id="element-5813">
            <h1 className="text-2xl font-bold text-gray-900" data-id="element-5814">
              {t('pages.DisputeDetail.dispute')}
            </h1>
            {getStatusBadge(effectiveStatus)}
          </div>
          <button onClick={() => setShowHelpModal(true)} className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700" data-id="element-5815">
            {isChallenged ? 'What happens after a dispute is challenged?' : 'How to respond to a dispute?'}
            <HelpCircleIcon size={16} data-id="element-5816" />
          </button>
        </div>

        <div className="mb-6" data-id="element-5817">
          <div className="text-sm text-gray-500" data-id="element-5818">
            {t('pages.ChallengeDispute.dispute_id')} {dispute.disputeId}
          </div>
        </div>

        {isChallenged && <div className="mb-6 p-5 bg-blue-50 border-2 border-blue-200 rounded-lg" data-id="element-5819">
            <div className="flex items-start gap-4" data-id="element-5820">
              <div className="w-10 h-10 rounded-full bg-blue-500 flex items-center justify-center flex-shrink-0" data-id="element-5821">
                <CheckCircleIcon size={20} className="text-white" data-id="element-5822" />
              </div>
              <div className="flex-1" data-id="element-5823">
                <div className="text-base font-semibold text-gray-900 mb-2" data-id="element-5824">
                  {t('pages.DisputeDetail.challenge_submitted')}
                </div>
                <div className="text-sm text-gray-700 mb-4" data-id="element-5825">
                  {t('pages.DisputeDetail.your_challenge_has_been_submitted_and')}
                </div>
                <button onClick={handleViewChallengeDetails} className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 shadow-sm transition-colors" data-id="element-5826">
                  <FileTextIcon size={18} data-id="element-5827" />
                  {t('pages.DisputeDetail.view_challenge_details')}
                </button>
              </div>
            </div>
          </div>}

        {dispute.status === 'won' && <div className="mb-6 p-5 bg-green-50 border-2 border-green-200 rounded-lg" data-id="element-5828">
            <div className="flex items-start gap-4" data-id="element-5829">
              <div className="w-10 h-10 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0" data-id="element-5830">
                <CheckCircleIcon size={20} className="text-white" data-id="element-5831" />
              </div>
              <div className="flex-1" data-id="element-5832">
                <div className="text-base font-semibold text-gray-900 mb-2" data-id="element-5833">
                  {t('pages.DisputeDetail.dispute_won')}
                </div>
                <div className="text-sm text-gray-700 mb-4" data-id="element-5834">
                  {t('pages.DisputeDetail.the_issuing_bank_has_reviewed_your')}
                </div>
                <button onClick={handleViewChallengeDetails} className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-white bg-green-600 rounded-lg hover:bg-green-700 shadow-sm transition-colors" data-id="element-5835">
                  <FileTextIcon size={18} data-id="element-5836" />
                  {t('pages.DisputeDetail.view_your_submitted_evidence')}
                </button>
              </div>
            </div>
          </div>}

        {dispute.status === 'reversed' && <div className="mb-6 p-5 bg-green-50 border-2 border-green-200 rounded-lg" data-id="element-5837">
            <div className="flex items-start gap-4" data-id="element-5838">
              <div className="w-10 h-10 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0" data-id="element-5839">
                <CheckCircleIcon size={20} className="text-white" data-id="element-5840" />
              </div>
              <div className="flex-1" data-id="element-5841">
                <div className="text-base font-semibold text-gray-900 mb-2" data-id="element-5842">
                  {t('pages.DisputeDetail.dispute_reversed')}
                </div>
                <div className="text-sm text-gray-700" data-id="element-5843">
                  {t('pages.DisputeDetail.the_cardholder_has_withdrawn_their_dispute')}
                </div>
              </div>
            </div>
          </div>}

        {dispute.status === 'accepted' && <div className="mb-6 p-5 bg-[#FEE7E5] border-2 border-[#FBD4D1] rounded-lg" data-id="element-5844">
            <div className="flex items-start gap-4" data-id="element-5845">
              <div className="w-10 h-10 rounded-full bg-[#E2231A] flex items-center justify-center flex-shrink-0" data-id="element-5846">
                <CheckCircleIcon size={20} className="text-gray-900" data-id="element-5847" />
              </div>
              <div className="flex-1" data-id="element-5848">
                <div className="text-base font-semibold text-gray-900 mb-2" data-id="element-5849">
                  {t('pages.DisputeDetail.dispute_accepted')}
                </div>
                <div className="text-sm text-gray-700" data-id="element-5850">
                  {t('pages.DisputeDetail.you_have_accepted_this_dispute_the')}
                </div>
              </div>
            </div>
          </div>}

        {isAccepted && <div className="mb-6 p-5 bg-[#FEE7E5] border-2 border-[#FBD4D1] rounded-lg" data-id="element-5851">
            <div className="flex items-start gap-4" data-id="element-5852">
              <div className="w-10 h-10 rounded-full bg-[#E2231A] flex items-center justify-center flex-shrink-0" data-id="element-5853">
                <CheckCircleIcon size={20} className="text-gray-900" data-id="element-5854" />
              </div>
              <div className="flex-1" data-id="element-5855">
                <div className="text-base font-semibold text-gray-900 mb-2" data-id="element-5856">
                  {t('pages.DisputeDetail.dispute_accepted')}
                </div>
                <div className="text-sm text-gray-700" data-id="element-5857">
                  {t('pages.DisputeDetail.you_have_accepted_this_dispute_the')}
                </div>
              </div>
            </div>
          </div>}

        {dispute.status === 'lost' && <div className="mb-6 p-5 bg-red-50 border-2 border-red-200 rounded-lg" data-id="element-5858">
            <div className="flex items-start gap-4" data-id="element-5859">
              <div className="w-10 h-10 rounded-full bg-red-500 flex items-center justify-center flex-shrink-0" data-id="element-5860">
                <XCircleIcon size={20} className="text-white" data-id="element-5861" />
              </div>
              <div className="flex-1" data-id="element-5862">
                <div className="text-base font-semibold text-gray-900 mb-2" data-id="element-5863">
                  {t('pages.DisputeDetail.dispute_lost')}
                </div>
                <div className="text-sm text-gray-700 mb-4" data-id="element-5864">
                  {t('pages.DisputeDetail.the_issuing_bank_has_reviewed_your_2')}
                </div>
                <button onClick={handleViewChallengeDetails} className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-white bg-red-600 rounded-lg hover:bg-red-700 shadow-sm transition-colors" data-id="element-5865">
                  <FileTextIcon size={18} data-id="element-5866" />
                  {t('pages.DisputeDetail.view_your_submitted_evidence')}
                </button>
              </div>
            </div>
          </div>}

        <div className="grid grid-cols-2 gap-8 mb-6" data-id="element-5867">
          <div data-id="element-5868">
            <div className="text-sm text-gray-500 mb-1" data-id="element-5869">
              {t('pages.ChallengeDispute.dispute_amount')}
            </div>
            <div className="text-2xl font-semibold text-gray-900" data-id="element-5870">
              {formatCurrency(dispute.amount)}
            </div>
          </div>
          <div data-id="element-5871">
            <div className="text-sm text-gray-500 mb-1" data-id="element-5872">
              {t('pages.ChallengeDispute.response_due')}
            </div>
            <div className="text-base font-medium text-gray-900" data-id="element-5873">
              {formatDateTime(dispute.dueDate)}
            </div>
            {hoursRemaining > 0 && hoursRemaining <= 24 && !isChallenged && <div className="text-sm text-red-600 font-medium" data-id="element-5874">
                {t('pages.DisputeDetail.due_in')} {hoursRemaining}{' '}
                {hoursRemaining === 1 ? 'hour' : 'hours'}
              </div>}
          </div>
        </div>

        {/* Purchasing-history challenge banner removed for demo */}

        {isChallenged && <div className="mb-6" data-id="element-5875">
            <button onClick={handleViewChallengeDetails} className="text-sm text-blue-600 hover:text-blue-700 font-medium" data-id="element-5876">
              {t('pages.DisputeDetail.view_challenge_details')}
            </button>
          </div>}

        <div className="grid grid-cols-2 gap-x-8 gap-y-4 text-sm" data-id="element-5877">
          <div data-id="element-5878">
            <div className="text-gray-500 mb-1" data-id="element-5879">
              {t('pages.DisputeDetail.reason')}
            </div>
            <div className="flex items-center gap-2" data-id="element-5880">
              <span className="font-medium text-gray-900" data-id="element-5881">
                {getReasonLabel(dispute.reason)}
              </span>
              <div className="relative inline-block" data-id="element-5882">
                <button onMouseEnter={() => setShowReasonTooltip(true)} onMouseLeave={() => setShowReasonTooltip(false)} className="inline-flex items-center" data-id="element-5883">
                  <InfoIcon size={14} className="text-gray-400 hover:text-gray-600" data-id="element-5884" />
                </button>
                {showReasonTooltip && <div className="absolute z-10 w-64 p-3 bg-gray-900 text-white text-xs rounded-lg shadow-lg bottom-full left-0 mb-2" data-id="element-5885">
                    {getReasonDescription(dispute.reason)}
                    <div className="absolute bottom-0 left-4 transform translate-y-1/2 rotate-45 w-2 h-2 bg-gray-900" data-id="element-5886"></div>
                  </div>}
              </div>
            </div>
          </div>
          <div data-id="element-5887">
            <div className="text-gray-500 mb-1" data-id="element-5888">
              {t('pages.DisputeDetail.network_reason_code')}
            </div>
            <div className="font-medium text-gray-900" data-id="element-5889">
              {dispute.networkReasonCode}
            </div>
          </div>
          <div data-id="element-5890">
            <div className="text-gray-500 mb-1" data-id="element-5891">
              {t('pages.DisputeDetail.dispute_stage')}
            </div>
            <div className="font-medium text-gray-900 capitalize" data-id="element-5892">
              {dispute.stage.replace(/_/g, ' ')}
            </div>
          </div>
          <div data-id="element-5893">
            <div className="text-gray-500 mb-1" data-id="element-5894">
              {t('pages.DisputeDetail.dispute_time')}
            </div>
            <div className="font-medium text-gray-900" data-id="element-5895">
              {formatDateTime(dispute.createdAt)}
            </div>
          </div>
          <div data-id="element-5896">
            <div className="text-gray-500 mb-1" data-id="element-5897">
              {t('pages.DisputeDetail.transaction_type')}
            </div>
            <div className="font-medium text-gray-900" data-id="element-5898">
              {t('pages.DisputeDetail.payment')}
            </div>
          </div>
          <div data-id="element-5899">
            <div className="text-gray-500 mb-1" data-id="element-5900">
              {t('pages.DisputeDetail.issuer_comments')}
            </div>
            <div className="font-medium text-gray-900" data-id="element-5901">
              {t('pages.DisputeDetail.recall')}
            </div>
          </div>
        </div>

        {dispute.status === 'requires_response' && !isChallenged && !isAccepted && <div className="flex items-center gap-3 mt-6 pt-6 border-t border-gray-200" data-id="element-5902">
              <button onClick={handleChallengeClick} className="px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700 border border-blue-600 hover:border-blue-700 rounded-md" data-id="element-5903">
                {t('pages.DisputeDetail.challenge')}
              </button>
              <button onClick={handleAcceptClick} className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md" data-id="element-5904">
                {t('pages.DisputeDetail.accept')}
              </button>
            </div>}
      </div>

      {/* Tabs Section */}
      <div className="bg-white rounded-lg border border-gray-200" data-id="element-5905">
        <div className="border-b border-gray-200" data-id="element-5906">
          <nav className="flex gap-8 px-6" role="tablist" aria-label={t('pages.DisputeDetail.attrs.tabs')} data-id="element-5907">
            <button role="tab" aria-selected={activeTab === 'payment'} aria-controls="dispute-tabpanel" id="dispute-tab-payment" onClick={() => setActiveTab('payment')} className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'payment' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-5908">
              {t('pages.DisputeDetail.payment_summary')}
            </button>
            <button role="tab" aria-selected={activeTab === 'related'} aria-controls="dispute-tabpanel" id="dispute-tab-related" onClick={() => setActiveTab('related')} className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'related' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-5909">
              {t('pages.DisputeDetail.related_orders')}
            </button>
          </nav>
        </div>

        {/* Payment Summary Tab Content */}
        {activeTab === 'payment' && <div className="p-6" data-id="element-5910">
            <div className="space-y-4 text-sm" data-id="element-5911">
              <div data-id="element-5912">
                <div className="text-gray-500 mb-1" data-id="element-5913">
                  {t('pages.DisputeDetail.order_id')}
                </div>
                <div className="flex items-center gap-2" data-id="element-5914">
                  <span className="font-medium text-gray-900" data-id="element-5915">
                    {dispute.transactionId}
                  </span>
                  <div className="relative group" data-id="element-5916">
                    <button onClick={() => handleCopyOrderId(dispute.transactionId)} className="text-gray-400 hover:text-blue-600 transition-colors" title={t('pages.DisputeDetail.attrs.copy_order_id')} aria-label={t('pages.DisputeDetail.attrs.copy_order_id')} data-id="element-5917">
                      {copiedOrderId === dispute.transactionId ? <CheckIcon size={16} className="text-green-600" aria-hidden="true" data-id="element-5918" /> : <CopyIcon size={16} aria-hidden="true" data-id="element-5919" />}
                    </button>
                    {copiedOrderId === dispute.transactionId && <div className="absolute left-1/2 -translate-x-1/2 -top-8 px-2 py-1 bg-gray-900 text-white text-xs rounded whitespace-nowrap" data-id="element-5920">
                        {t('pages.DisputeDetail.copied')}
                      </div>}
                  </div>
                </div>
              </div>
              <div data-id="element-5921">
                <div className="text-gray-500 mb-1" data-id="element-5922">
                  {t('pages.DisputeDetail.source')}
                </div>
                <div className="flex items-center gap-2" data-id="element-5923">
                  {(() => {
                const {
                  name,
                  Icon
                } = getPaymentSource();
                return <>
                        <Icon size={16} className="text-gray-600" data-id="element-5924" />
                        <span className="font-medium text-gray-900" data-id="element-5925">
                          {name}
                        </span>
                      </>;
              })()}
                </div>
              </div>
              <div data-id="element-5926">
                <div className="text-gray-500 mb-1" data-id="element-5927">
                  {t('pages.DisputeDetail.payment_type')}
                </div>
                <div className="font-medium text-gray-900" data-id="element-5928">
                  {t('pages.DisputeDetail.debit_card')}
                </div>
              </div>
              <div data-id="element-5929">
                <div className="text-gray-500 mb-1" data-id="element-5930">
                  {t('pages.DisputeDetail.payment_method')}
                </div>
                <div className="flex items-center gap-2" data-id="element-5931">
                  <span className="font-medium text-gray-900" data-id="element-5932">
                    {dispute.cardBrand} •••• {dispute.cardLast4}
                  </span>
                </div>
              </div>
              <div data-id="element-5933">
                <div className="text-gray-500 mb-1" data-id="element-5934">
                  {t('pages.DisputeDetail.cardholder_name')}
                </div>
                <div className="font-medium text-gray-900" data-id="element-5935">
                  {dispute.customerName}
                </div>
              </div>
              <div data-id="element-5936">
                <div className="text-gray-500 mb-1" data-id="element-5937">
                  {t('pages.ChallengeDispute.customer_purchase_ip')}
                </div>
                <div className="font-medium text-gray-900" data-id="element-5938">35.221.170.121</div>
              </div>
              <div data-id="element-5939">
                <div className="text-gray-500 mb-1" data-id="element-5940">
                  {t('pages.DisputeDetail.customer_phone')}
                </div>
                <div className="font-medium text-gray-900" data-id="element-5941">+65 9123 4567</div>
              </div>
              <div data-id="element-5942">
                <div className="text-gray-500 mb-1" data-id="element-5943">
                  {t('pages.DisputeDetail.security_checks')}
                </div>
                <div className="space-y-1" data-id="element-5944">
                  <div className="flex items-center gap-2 text-green-600" data-id="element-5945">
                    <CheckCircleIcon size={16} data-id="element-5946" />
                    <span data-id="element-5947">{t('pages.DisputeDetail.card_security_code')}</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-400" data-id="element-5948">
                    <XCircleIcon size={16} data-id="element-5949" />
                    <span data-id="element-5950">{t('pages.DisputeDetail.3ds')}</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-400" data-id="element-5951">
                    <XCircleIcon size={16} data-id="element-5952" />
                    <span data-id="element-5953">
                      {t('pages.DisputeDetail.billing_address_verification')}
                    </span>
                  </div>
                </div>
              </div>
              <a href="#" className="text-blue-600 hover:text-blue-700 text-sm font-medium inline-block" data-id="element-5954">
                {t('pages.DisputeDetail.view_payment_details')}
              </a>
            </div>
          </div>}

        {/* Related Orders Tab Content */}
        {activeTab === 'related' && <div className="p-6" data-id="element-5955">
            <div className="mb-4 text-sm text-gray-600" data-id="element-5956">
              {t('pages.DisputeDetail.customers_may_have_a_purchasing_history')}{' '}
              <span className="font-medium" data-id="element-5957">
                {t('pages.DisputeDetail.matching_transactions_to_the_issuer')}
              </span>
              .{' '}
              <div className="relative inline-block" data-id="element-5958">
                <button onMouseEnter={() => setShowMatchingCriteriaTooltip(true)} onMouseLeave={() => setShowMatchingCriteriaTooltip(false)} className="inline-flex items-center" data-id="element-5959">
                  <InfoIcon size={14} className="text-gray-400 hover:text-gray-600" data-id="element-5960" />
                </button>
                {showMatchingCriteriaTooltip && <div className="absolute z-10 w-96 p-4 bg-gray-900 text-white text-xs rounded-lg shadow-lg bottom-full left-0 mb-2" data-id="element-5961">
                    <div className="space-y-2" data-id="element-5962">
                      <p data-id="element-5963">
                        {t('pages.DisputeDetail.to_qualify_as_a_matching_transaction')}
                      </p>
                      <ul className="list-disc pl-4 space-y-1" data-id="element-5964">
                        <li data-id="element-5965">
                          {t('pages.DisputeDetail.at_least_two_data_elements_ip')}
                        </li>
                        <li data-id="element-5966">
                          {t('pages.DisputeDetail.between_120_and_365_days_older')}
                        </li>
                        <li data-id="element-5967">
                          {t('pages.DisputeDetail.undisputed_and_not_have_been_reported')}
                        </li>
                      </ul>
                      <p data-id="element-5968">{t('pages.DisputeDetail.we_also_show_you_if_the')}</p>
                    </div>
                    <div className="absolute bottom-0 left-4 transform translate-y-1/2 rotate-45 w-2 h-2 bg-gray-900" data-id="element-5969"></div>
                  </div>}
              </div>
            </div>

            {/* Related Orders Table */}
            <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-5970">
              <table className="min-w-full divide-y divide-gray-200" data-id="element-5971">
                <thead className="bg-gray-50" data-id="element-5972">
                  <tr data-id="element-5973">
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-5974">
                      {t('pages.DisputeDetail.order_id')}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-5975">
                      {t('pages.DisputeDetail.amount')}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-5976">
                      {t('pages.DisputeDetail.created_time')}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-5977">
                      {t('pages.DisputeDetail.status')}
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-5978">
                      {t('pages.DisputeDetail.matching_criteria')}
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200" data-id="element-5979">
                  {paginatedOrders.map(order => <tr key={order.id} className="hover:bg-gray-50" data-id="element-5980">
                      <td className="px-4 py-3" data-id="element-5981">
                        <div className="flex items-center gap-2" data-id="element-5982">
                          <span className="text-sm text-gray-900" data-id="element-5983">
                            {order.orderId}
                          </span>
                          <button className="text-blue-600 hover:text-blue-700" data-id="element-5984">
                            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-5985">
                              <path d="M13.3333 6H7.33333C6.59695 6 6 6.59695 6 7.33333V13.3333C6 14.0697 6.59695 14.6667 7.33333 14.6667H13.3333C14.0697 14.6667 14.6667 14.0697 14.6667 13.3333V7.33333C14.6667 6.59695 14.0697 6 13.3333 6Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-5986" />

                              <path d="M3.33333 10H2.66667C2.31304 10 1.97391 9.85952 1.72386 9.60947C1.47381 9.35942 1.33333 9.02029 1.33333 8.66667V2.66667C1.33333 2.31304 1.47381 1.97391 1.72386 1.72386C1.97391 1.47381 2.31304 1.33333 2.66667 1.33333H8.66667C9.02029 1.33333 9.35942 1.47381 9.60947 1.72386C9.85952 1.97391 10 2.31304 10 2.66667V3.33333" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-5987" />
                            </svg>
                          </button>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900" data-id="element-5988">
                        {formatCurrency(order.amount, order.currency)}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900" data-id="element-5989">
                        {order.createdTime}
                      </td>
                      <td className="px-4 py-3" data-id="element-5990">
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800" data-id="element-5991">
                          {order.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right" data-id="element-5992">
                        <button onClick={() => setSelectedOrder(order)} className="text-sm text-blue-600 hover:text-blue-700 font-medium" data-id="element-5993">
                          {order.matchingCriteria} {t('pages.DisputeDetail.of')}{' '}
                          {order.totalCriteria}{' '}
                          {t('pages.DisputeDetail.criteria')}
                        </button>
                      </td>
                    </tr>)}
                </tbody>
              </table>

              {/* Pagination */}
              <div className="bg-gray-50 px-4 py-3 flex items-center justify-between border-t border-gray-200" data-id="element-5994">
                <div className="flex items-center gap-2" data-id="element-5995">
                  <button onClick={() => setCurrentPage(1)} disabled={currentPage === 1} className="p-1 rounded hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-5996">
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-5997">
                      <path d="M11 12L7 8L11 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-5998" />

                      <path d="M5 12L5 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-5999" />
                    </svg>
                  </button>
                  <button onClick={() => setCurrentPage(Math.max(1, currentPage - 1))} disabled={currentPage === 1} className="p-1 rounded hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-6000">
                    <ChevronLeftIcon size={16} data-id="element-6001" />
                  </button>
                  {Array.from({
                length: totalPages
              }, (_, i) => i + 1).map(page => <button key={page} onClick={() => setCurrentPage(page)} className={`px-3 py-1 text-sm rounded ${currentPage === page ? 'bg-blue-600 text-white' : 'hover:bg-gray-200 text-gray-700'}`} data-id="element-6002">
                      {page}
                    </button>)}
                  <button onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))} disabled={currentPage === totalPages} className="p-1 rounded hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-6003">
                    <ChevronRightIcon size={16} data-id="element-6004" />
                  </button>
                  <button onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} className="p-1 rounded hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-6005">
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-6006">
                      <path d="M5 4L9 8L5 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-6007" />

                      <path d="M11 4L11 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-6008" />
                    </svg>
                  </button>
                </div>
                <div className="flex items-center gap-4" data-id="element-6009">
                  <div className="text-sm text-gray-700" data-id="element-6010">
                    {t('pages.DisputeDetail.rows_per_page')}{' '}
                    <span className="font-medium" data-id="element-6011">{rowsPerPage}</span>
                  </div>
                  <div className="text-sm text-gray-700" data-id="element-6012">
                    {(currentPage - 1) * rowsPerPage + 1} -{' '}
                    {Math.min(currentPage * rowsPerPage, relatedOrders.length)}{' '}
                    {t('pages.DisputeDetail.of')} {relatedOrders.length}
                  </div>
                </div>
              </div>
            </div>
          </div>}
      </div>

      {/* Payment Breakdown Section */}
      <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-6013">
        <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-6014">
          {t('pages.DisputeDetail.payment_breakdown')}
        </h3>
        <div className="space-y-3 max-w-md" data-id="element-6015">
          <div className="flex items-center justify-between" data-id="element-6016">
            <div className="text-sm text-gray-600" data-id="element-6017">
              {t('pages.DisputeDetail.payment_amount')}
            </div>
            <div className="text-sm font-medium text-gray-900" data-id="element-6018">
              {formatCurrency(paymentAmount)}
            </div>
          </div>
          <div className="flex items-center justify-between" data-id="element-6019">
            <div className="flex items-center gap-1" data-id="element-6020">
              <span className="text-sm text-gray-600" data-id="element-6021">
                {t('pages.DisputeDetail.final_disputed_amount')}
              </span>
              <InfoIcon size={14} className="text-gray-400" data-id="element-6022" />
            </div>
            <div className="text-sm font-medium text-gray-900" data-id="element-6023">
              - {formatCurrency(disputedAmount)}
            </div>
          </div>
          <div className="flex items-center justify-between" data-id="element-6024">
            <div className="flex items-center gap-1" data-id="element-6025">
              <span className="text-sm text-gray-600" data-id="element-6026">
                {t('pages.DisputeDetail.dispute_fee')}
              </span>
              <InfoIcon size={14} className="text-gray-400" data-id="element-6027" />
            </div>
            <div className="text-sm font-medium text-gray-900" data-id="element-6028">
              - {formatCurrency(disputeFee)}
            </div>
          </div>
          <div className="pt-3 border-t border-gray-200" data-id="element-6029">
            <div className="flex items-center justify-between" data-id="element-6030">
              <div className="text-sm font-semibold text-gray-900" data-id="element-6031">
                {t('pages.DisputeDetail.net_amount')}
              </div>
              <div className="text-sm font-semibold text-gray-900" data-id="element-6032">
                - {formatCurrency(netAmount)}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-6033">
        <div className="flex items-center justify-between mb-4" data-id="element-6034">
          <h3 className="text-lg font-semibold text-gray-900" data-id="element-6035">
            {t('pages.DisputeDetail.recent_activity')}
          </h3>
          <button onClick={() => setShowAddNote(!showAddNote)} className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700 border border-blue-600 hover:border-blue-700 rounded-md transition-colors" data-id="element-6036">
            <PenIcon size={16} data-id="element-6037" />
            {t('pages.DisputeDetail.add_note')}
          </button>
        </div>

        {/* Add Note Form */}
        {showAddNote && <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200" data-id="element-6038">
            <textarea value={noteText} onChange={e => setNoteText(e.target.value)} placeholder={t('pages.DisputeDetail.attrs.add_a_note_for_internal_reference')} rows={3} className="w-full border border-gray-300 rounded-md p-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 mb-3" data-id="element-6039" />

            <div className="flex items-center justify-end gap-2" data-id="element-6040">
              <button onClick={() => {
            setShowAddNote(false);
            setNoteText('');
          }} className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900" data-id="element-6041">
                {t('pages.ChallengeDispute.cancel')}
              </button>
              <button onClick={handleAddNote} disabled={!noteText.trim()} className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-6042">
                {t('pages.DisputeDetail.add_note')}
              </button>
            </div>
          </div>}

        <div className="space-y-4" data-id="element-6043">
          {reversedLifecycle.map((event, index) => <div key={event.id} className="flex gap-4" data-id="element-6044">
              <div className="flex flex-col items-center" data-id="element-6045">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${event.status === 'note' ? 'bg-purple-100' : index === 0 ? 'bg-blue-100' : 'bg-gray-100'}`} data-id="element-6046">
                  {event.status === 'note' ? <FileTextIcon size={16} className="text-purple-600" data-id="element-6047" /> : <ClockIcon size={16} className={index === 0 ? 'text-blue-600' : 'text-gray-400'} data-id="element-6048" />}
                </div>
                {index < reversedLifecycle.length - 1 && <div className="w-0.5 h-12 bg-gray-200 my-1" data-id="element-6049"></div>}
              </div>
              <div className="flex-1 pb-4" data-id="element-6050">
                <div className="flex items-start justify-between gap-2" data-id="element-6051">
                  <div className="flex-1" data-id="element-6052">
                    {event.status === 'note' && <div className="flex items-center gap-2 mb-1" data-id="element-6053">
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-purple-100 text-purple-700 text-xs font-medium rounded" data-id="element-6054">
                          {event.isPinned && <PinIcon size={12} data-id="element-6055" />}
                          {t('pages.DisputeDetail.internal_note')}
                        </span>
                      </div>}
                    {editingNoteId === event.id ? <div className="space-y-2" data-id="element-6056">
                        <textarea value={editingNoteText} onChange={e => setEditingNoteText(e.target.value)} rows={3} className="w-full border border-gray-300 rounded-md p-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500" data-id="element-6057" />

                        <div className="flex items-center gap-2" data-id="element-6058">
                          <button onClick={() => handleSaveEdit(event.id)} className="px-3 py-1 text-xs font-medium text-white bg-blue-600 rounded hover:bg-blue-700" data-id="element-6059">
                            {t('pages.DisputeDetail.save')}
                          </button>
                          <button onClick={handleCancelEdit} className="px-3 py-1 text-xs font-medium text-gray-700 hover:text-gray-900" data-id="element-6060">
                            {t('pages.ChallengeDispute.cancel')}
                          </button>
                        </div>
                      </div> : <>
                        <div className="text-sm font-medium text-gray-900" data-id="element-6061">
                          {event.description}
                        </div>
                        <div className="text-xs text-gray-500 mt-1" data-id="element-6062">
                          {formatDateTime(event.timestamp)}
                          {event.status === 'note' && event.author && <span data-id="element-6063">
                              {' '}
                              {t('pages.DisputeDetail.by')} {event.author}
                            </span>}
                        </div>
                      </>}
                  </div>
                  {event.status === 'note' && editingNoteId !== event.id && <div className="relative note-menu-container" data-id="element-6064">
                      <button onClick={() => setShowNoteMenu(showNoteMenu === event.id ? null : event.id)} aria-label="Note actions" aria-expanded={showNoteMenu === event.id} className="p-1 text-gray-400 hover:text-gray-600 rounded hover:bg-gray-100" data-id="element-6065">
                        <MoreVerticalIcon size={16} aria-hidden="true" data-id="element-6066" />
                      </button>
                      {showNoteMenu === event.id && <div className="absolute right-0 top-8 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-10" data-id="element-6067">
                          <button onClick={() => handlePinNote(event.id)} className="w-full px-4 py-2 text-left text-sm text-blue-600 hover:bg-gray-50 flex items-center gap-2" data-id="element-6068">
                            <PinIcon size={14} data-id="element-6069" />
                            {event.isPinned ? 'Unpin from top' : 'Pin to the top'}
                          </button>
                          <button onClick={() => handleEditNote(event.id)} className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2" data-id="element-6070">
                            <PenIcon size={14} data-id="element-6071" />
                            {t('pages.DisputeDetail.edit')}
                          </button>
                          <button onClick={() => handleDeleteNote(event.id)} className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-gray-50 flex items-center gap-2" data-id="element-6072">
                            <TrashIcon size={14} data-id="element-6073" />
                            {t('pages.DisputeDetail.delete')}
                          </button>
                        </div>}
                    </div>}
                </div>
              </div>
            </div>)}
        </div>
      </div>

      {/* Challenge Modal */}
      {showChallengeModal && <div className="fixed inset-0 z-50 overflow-y-auto" data-id="element-6074">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0" data-id="element-6075">
            <div className="fixed inset-0 transition-opacity" onClick={() => setShowChallengeModal(false)} data-id="element-6076">
              <div className="absolute inset-0 bg-gray-500 opacity-75" data-id="element-6077"></div>
            </div>

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true" data-id="element-6078">
              &#8203;
            </span>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full" data-id="element-6079">
              <div className="bg-white px-6 pt-6 pb-4" data-id="element-6080">
                <div className="flex items-start justify-between mb-4" data-id="element-6081">
                  <h2 className="text-xl font-bold text-gray-900" data-id="element-6082">
                    {t('pages.DisputeDetail.you_are_challenging_the_dispute')}
                  </h2>
                  <button onClick={() => setShowChallengeModal(false)} className="text-gray-400 hover:text-gray-600" data-id="element-6083">
                    <CloseIcon size={20} data-id="element-6084" />
                  </button>
                </div>

                <p className="text-sm text-gray-600 mb-6" data-id="element-6085">
                  {t('pages.DisputeDetail.heres_a_preview_of_what_will')}
                </p>

                <div className="space-y-6" data-id="element-6086">
                  <div className="flex gap-4" data-id="element-6087">
                    <div className="flex flex-col items-center" data-id="element-6088">
                      <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0" data-id="element-6089">
                        <PenIcon size={20} className="text-blue-600" data-id="element-6090" />
                      </div>
                      <div className="w-0.5 h-full bg-gray-200 mt-2" data-id="element-6091"></div>
                    </div>
                    <div className="flex-1 pb-6" data-id="element-6092">
                      <h3 className="text-sm font-semibold text-gray-900 mb-1" data-id="element-6093">
                        {t('pages.DisputeDetail.you_submit_the_challenge')}
                      </h3>
                      <p className="text-sm text-gray-600" data-id="element-6094">
                        {t('pages.DisputeDetail.well_guide_you_to_provide_the')}
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-4" data-id="element-6095">
                    <div className="flex flex-col items-center" data-id="element-6096">
                      <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0" data-id="element-6097">
                        <div className="w-3 h-3 rounded-full border-2 border-gray-400" data-id="element-6098"></div>
                      </div>
                    </div>
                    <div className="flex-1" data-id="element-6099">
                      <h3 className="text-sm font-semibold text-gray-900 mb-1" data-id="element-6100">
                        {t('pages.DisputeDetail.issuer_reviews_the_evidence_decides_on')}
                      </h3>
                      <p className="text-sm text-gray-600 mb-2" data-id="element-6101">
                        {t('pages.DisputeDetail.if_the_issuer_is_convinced_by')}
                      </p>
                      <p className="text-sm text-gray-600" data-id="element-6102">
                        {t('pages.DisputeDetail.if_they_are_not_convinced_by')}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 px-6 py-4 flex items-center justify-end gap-3" data-id="element-6103">
                <button onClick={() => setShowChallengeModal(false)} className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900" data-id="element-6104">
                  {t('pages.ChallengeDispute.cancel')}
                </button>
                <button onClick={handleChallengeConfirm} className="px-6 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700" data-id="element-6105">
                  {t('pages.DisputeDetail.challenge')}
                </button>
              </div>
            </div>
          </div>
        </div>}

      {/* Evidence Form Modal - keeping existing */}
      {showEvidenceForm && <div className="fixed inset-0 z-50 overflow-y-auto" data-id="element-6106">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0" data-id="element-6107">
            <div className="fixed inset-0 transition-opacity" onClick={() => setShowEvidenceForm(false)} data-id="element-6108">
              <div className="absolute inset-0 bg-gray-500 opacity-75" data-id="element-6109"></div>
            </div>

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true" data-id="element-6110">
              &#8203;
            </span>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full" data-id="element-6111">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4" data-id="element-6112">
                <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4" data-id="element-6113">
                  {t('pages.DisputeDetail.submit_evidence')}
                </h3>
                <div className="space-y-4" data-id="element-6114">
                  <div data-id="element-6115">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-6116">
                      {t('pages.DisputeDetail.customer_communication')}
                    </label>
                    <textarea rows={3} className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500" placeholder={t('pages.DisputeDetail.attrs.describe_any_communication_with_the_customer')} data-id="element-6117" />
                  </div>
                  <div data-id="element-6118">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-6119">
                      {t('pages.DisputeDetail.shippingdelivery_documentation')}
                    </label>
                    <textarea rows={2} className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500" placeholder={t('pages.DisputeDetail.attrs.tracking_numbers_delivery_confirmation_etc')} data-id="element-6120" />
                  </div>
                  <div data-id="element-6121">
                    <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-6122">
                      {t('pages.DisputeDetail.upload_supporting_documents')}
                    </label>
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center" data-id="element-6123">
                      <UploadIcon size={32} className="mx-auto text-gray-400 mb-2" data-id="element-6124" />

                      <div className="text-sm text-gray-600" data-id="element-6125">
                        {t('pages.DisputeDetail.drag_and_drop_files_here_or')}
                      </div>
                      <div className="text-xs text-gray-500 mt-1" data-id="element-6126">
                        {t('pages.DisputeDetail.pdf_jpg_png_up_to_10mb')}
                      </div>
                      <input type="file" multiple className="hidden" aria-label="Upload evidence files" data-id="element-6127" />
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse" data-id="element-6128">
                <button type="button" className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm" onClick={() => setShowEvidenceForm(false)} data-id="element-6129">
                  {t('pages.DisputeDetail.submit_evidence')}
                </button>
                <button type="button" className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm" onClick={() => setShowEvidenceForm(false)} data-id="element-6130">
                  {t('pages.ChallengeDispute.cancel')}
                </button>
              </div>
            </div>
          </div>
        </div>}

      {/* Matching Criteria Side Panel */}
      {selectedOrder && <div className="fixed inset-0 z-50 overflow-hidden" data-id="element-6131">
          <div className="absolute inset-0 overflow-hidden" data-id="element-6132">
            <div className="absolute inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setSelectedOrder(null)} data-id="element-6133"></div>
            <div className="fixed inset-y-0 right-0 pl-10 max-w-full flex" data-id="element-6134">
              <div className="w-screen max-w-md" data-id="element-6135">
                <div className="h-full flex flex-col bg-white shadow-xl overflow-y-auto" data-id="element-6136">
                  {/* Header */}
                  <div className="px-6 py-4 border-b border-gray-200" data-id="element-6137">
                    <div className="flex items-center justify-between" data-id="element-6138">
                      <h2 className="text-lg font-semibold text-gray-900" data-id="element-6139">
                        {t('pages.DisputeDetail.related_order')}
                      </h2>
                      <button onClick={() => setSelectedOrder(null)} className="text-gray-400 hover:text-gray-600" data-id="element-6140">
                        <CloseIcon size={20} data-id="element-6141" />
                      </button>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="flex-1 px-6 py-4" data-id="element-6142">
                    {/* Order Status */}
                    <div className="mb-6" data-id="element-6143">
                      <div className="text-sm font-medium text-gray-900 mb-2" data-id="element-6144">
                        {t('pages.DisputeDetail.order')}
                      </div>
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800 uppercase" data-id="element-6145">
                        {selectedOrder.status}
                      </span>
                    </div>

                    {/* Order ID */}
                    <div className="mb-6" data-id="element-6146">
                      <div className="text-sm text-gray-500 mb-1" data-id="element-6147">
                        {t('pages.DisputeDetail.order_id')}
                      </div>
                      <div className="flex items-center gap-2" data-id="element-6148">
                        <span className="text-sm font-medium text-gray-900" data-id="element-6149">
                          {selectedOrder.orderId}
                        </span>
                        <button className="text-blue-600 hover:text-blue-700" data-id="element-6150">
                          <svg width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-6151">
                            <path d="M13.3333 6H7.33333C6.59695 6 6 6.59695 6 7.33333V13.3333C6 14.0697 6.59695 14.6667 7.33333 14.6667H13.3333C14.0697 14.6667 14.6667 14.0697 14.6667 13.3333V7.33333C14.6667 6.59695 14.0697 6 13.3333 6Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-6152" />

                            <path d="M3.33333 10H2.66667C2.31304 10 1.97391 9.85952 1.72386 9.60947C1.47381 9.35942 1.33333 9.02029 1.33333 8.66667V2.66667C1.33333 2.31304 1.47381 1.97391 1.72386 1.72386C1.97391 1.47381 2.31304 1.33333 2.66667 1.33333H8.66667C9.02029 1.33333 9.35942 1.47381 9.60947 1.72386C9.85952 1.97391 10 2.31304 10 2.66667V3.33333" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-6153" />
                          </svg>
                        </button>
                      </div>
                    </div>

                    {/* Matching Criteria */}
                    <div className="mb-6" data-id="element-6154">
                      <div className="flex items-center gap-2 mb-3" data-id="element-6155">
                        <h3 className="text-sm font-semibold text-gray-900" data-id="element-6156">
                          {t('pages.DisputeDetail.matching_criteria_2')}
                        </h3>
                        <InfoIcon size={14} className="text-gray-400" data-id="element-6157" />
                      </div>

                      <div className="space-y-6" data-id="element-6158">
                        {matchingCriteriaDetails.map((category, idx) => <div key={idx} data-id="element-6159">
                            <div className="text-sm font-medium text-gray-900 mb-1" data-id="element-6160">
                              {category.category}
                            </div>
                            {category.subtitle && <div className="text-xs text-gray-500 mb-2" data-id="element-6161">
                                {category.subtitle}
                              </div>}
                            <div className="space-y-2" data-id="element-6162">
                              {category.items.map((item, itemIdx) => <div key={itemIdx} className="flex items-center gap-2" data-id="element-6163">
                                  {item.matched ? <CheckCircleIcon size={16} className="text-green-600" data-id="element-6164" /> : <XCircleIcon size={16} className="text-red-600" data-id="element-6165" />}
                                  <span className="text-sm text-gray-700" data-id="element-6166">
                                    {item.label}
                                  </span>
                                </div>)}
                            </div>
                          </div>)}
                      </div>
                    </div>

                    {/* Payment Summary */}
                    <div className="border-t border-gray-200 pt-6" data-id="element-6167">
                      <h3 className="text-sm font-semibold text-gray-900 mb-4" data-id="element-6168">
                        {t('pages.DisputeDetail.payment_summary')}
                      </h3>
                      <div className="space-y-3 text-sm" data-id="element-6169">
                        <div className="flex justify-between" data-id="element-6170">
                          <span className="text-gray-600" data-id="element-6171">
                            {t('pages.DisputeDetail.payment_created_time')}
                          </span>
                          <span className="text-gray-900" data-id="element-6172">
                            2026-04-15
                            <br data-id="element-6173" />
                            {t('pages.DisputeDetail.0901_utc0800')}
                          </span>
                        </div>
                        <div className="flex justify-between" data-id="element-6174">
                          <span className="text-gray-600" data-id="element-6175">
                            {t('pages.DisputeDetail.payment_amount')}
                          </span>
                          <span className="text-gray-900" data-id="element-6176">
                            {formatCurrency(selectedOrder.amount, selectedOrder.currency)}
                          </span>
                        </div>
                        <div className="flex justify-between" data-id="element-6177">
                          <span className="text-gray-600" data-id="element-6178">
                            {t('pages.DisputeDetail.card_type')}
                          </span>
                          <span className="text-gray-900" data-id="element-6179">
                            {t('pages.DisputeDetail.credit')}
                          </span>
                        </div>
                        <div className="flex justify-between" data-id="element-6180">
                          <span className="text-gray-600" data-id="element-6181">
                            {t('pages.DisputeDetail.payment_method')}
                          </span>
                          <div className="flex items-center gap-2" data-id="element-6182">
                            <span className="text-blue-600 font-medium" data-id="element-6183">
                              {t('pages.DisputeDetail.visa')}
                            </span>
                            <span className="text-gray-900" data-id="element-6184">
                              {t('pages.DisputeDetail.visa_9400')}
                            </span>
                          </div>
                        </div>
                        <div className="flex justify-between" data-id="element-6185">
                          <span className="text-gray-600" data-id="element-6186">
                            {t('pages.DisputeDetail.cardholder_name')}
                          </span>
                          <span className="text-gray-900" data-id="element-6187">
                            {t('pages.DisputeDetail.auto_testing')}
                          </span>
                        </div>
                        <div data-id="element-6188">
                          <div className="text-gray-600 mb-2" data-id="element-6189">
                            {t('pages.DisputeDetail.payment_security_check')}
                          </div>
                          <div className="space-y-1" data-id="element-6190">
                            <div className="flex items-center gap-2" data-id="element-6191">
                              <CheckCircleIcon size={14} className="text-green-600" data-id="element-6192" />

                              <span className="text-gray-700 text-xs" data-id="element-6193">
                                {t('pages.DisputeDetail.card_security_code')}
                              </span>
                            </div>
                            <div className="flex items-center gap-2" data-id="element-6194">
                              <CheckCircleIcon size={14} className="text-green-600" data-id="element-6195" />

                              <span className="text-gray-700 text-xs" data-id="element-6196">
                                {t('pages.DisputeDetail.3ds')}
                              </span>
                            </div>
                            <div className="flex items-center gap-2" data-id="element-6197">
                              <XCircleIcon size={14} className="text-gray-400" data-id="element-6198" />

                              <span className="text-gray-700 text-xs" data-id="element-6199">
                                {t('pages.DisputeDetail.billing_address_verification')}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Footer */}
                    <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between" data-id="element-6200">
                      <button onClick={() => setSelectedOrder(null)} className="text-sm text-blue-600 hover:text-blue-700 font-medium" data-id="element-6201">
                        {t('pages.DisputeDetail.close')}
                      </button>
                      <button className="text-sm text-blue-600 hover:text-blue-700 font-medium" data-id="element-6202">
                        {t('pages.DisputeDetail.view_payment_details')}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>}

      {/* Challenge Details Modal */}
      {showChallengeDetails && <div className="fixed inset-0 z-50 overflow-y-auto" data-id="element-6203">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0" data-id="element-6204">
            <div className="fixed inset-0 transition-opacity" onClick={() => setShowChallengeDetails(false)} data-id="element-6205">
              <div className="absolute inset-0 bg-gray-500 opacity-75" data-id="element-6206"></div>
            </div>

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true" data-id="element-6207">
              &#8203;
            </span>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-3xl sm:w-full" data-id="element-6208">
              <div className="bg-white px-8 pt-8 pb-6" data-id="element-6209">
                <div className="flex items-start justify-between mb-6" data-id="element-6210">
                  <h2 className="text-2xl font-bold text-gray-900" data-id="element-6211">
                    {t('pages.ChallengeDispute.challenge_details')}
                  </h2>
                  <button onClick={() => setShowChallengeDetails(false)} className="text-gray-400 hover:text-gray-600" data-id="element-6212">
                    <CloseIcon size={24} data-id="element-6213" />
                  </button>
                </div>

                <div className="space-y-6" data-id="element-6214">
                  <div data-id="element-6215">
                    <h3 className="text-base font-semibold text-gray-900 mb-4" data-id="element-6216">
                      {t('pages.ChallengeDispute.challenge_details')}
                    </h3>
                    <div className="space-y-3" data-id="element-6217">
                      <div className="flex" data-id="element-6218">
                        <span className="text-sm text-gray-600 w-48 flex-shrink-0" data-id="element-6219">
                          {t('pages.ChallengeDispute.reason_for_challenge')}
                        </span>
                        <span className="text-sm text-gray-900" data-id="element-6220">
                          {t('pages.ChallengeDispute.the_customer_has_a_purchasing_history')}
                        </span>
                      </div>
                      <div className="flex" data-id="element-6221">
                        <span className="text-sm text-gray-600 w-48 flex-shrink-0" data-id="element-6222">
                          {t('pages.ChallengeDispute.type_of_product_or_service')}
                        </span>
                        <span className="text-sm text-gray-900" data-id="element-6223">
                          {t('pages.ChallengeDispute.physical_product')}
                        </span>
                      </div>
                      <div className="flex" data-id="element-6224">
                        <span className="text-sm text-gray-600 w-48 flex-shrink-0" data-id="element-6225">
                          {t('pages.ChallengeDispute.supporting_evidence')}
                        </span>
                        <div className="flex-1" data-id="element-6226">
                          <div className="flex items-center gap-3" data-id="element-6227">
                            <div className="w-10 h-10 bg-gray-100 rounded flex items-center justify-center flex-shrink-0" data-id="element-6228">
                              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-6229">
                                <path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-6230" />

                                <path d="M14 2V8H20" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-6231" />
                              </svg>
                            </div>
                            <div className="flex-1" data-id="element-6232">
                              <div className="text-sm font-medium text-gray-900" data-id="element-6233">
                                {t('pages.DisputeDetail.ohmmcairospecsshe9jul25pdf')}
                              </div>
                              <div className="text-sm text-gray-500" data-id="element-6234">
                                {t('pages.DisputeDetail.104_mb')}
                              </div>
                            </div>
                            <button className="text-blue-600 hover:text-blue-700" data-id="element-6235">
                              <EyeIcon size={18} data-id="element-6236" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div data-id="element-6237">
                    <h3 className="text-base font-semibold text-gray-900 mb-4" data-id="element-6238">
                      {t('pages.ChallengeDispute.transaction_details')}
                    </h3>
                    <div className="space-y-3" data-id="element-6239">
                      <div className="flex" data-id="element-6240">
                        <span className="text-sm text-gray-600 w-48 flex-shrink-0" data-id="element-6241">
                          {t('pages.ChallengeDispute.product_details')}
                        </span>
                        <span className="text-sm text-gray-900" data-id="element-6242">
                          {t('pages.ChallengeDispute.iphone7_5')}{' '}
                          {formatCurrency(dispute.amount)}
                        </span>
                      </div>
                      <div className="flex" data-id="element-6243">
                        <span className="text-sm text-gray-600 w-48 flex-shrink-0" data-id="element-6244">
                          {t('pages.ChallengeDispute.shipping_address')}
                        </span>
                        <span className="text-sm text-gray-900" data-id="element-6245">
                          {t('pages.DisputeDetail.pomelo_pay_office_1_raffles_place')}
                        </span>
                      </div>
                      <div className="flex" data-id="element-6246">
                        <span className="text-sm text-gray-600 w-48 flex-shrink-0" data-id="element-6247">
                          {t('pages.ChallengeDispute.customer_name')}
                        </span>
                        <span className="text-sm text-gray-900" data-id="element-6248">
                          {t('pages.ChallengeDispute.test_name')}
                        </span>
                      </div>
                      <div className="flex" data-id="element-6249">
                        <span className="text-sm text-gray-600 w-48 flex-shrink-0" data-id="element-6250">
                          {t('pages.ChallengeDispute.customer_email')}
                        </span>
                        <span className="text-sm text-gray-900" data-id="element-6251">
                          {t('pages.DisputeDetail.customerpomelopaycom')}
                        </span>
                      </div>
                      <div className="flex" data-id="element-6252">
                        <span className="text-sm text-gray-600 w-48 flex-shrink-0" data-id="element-6253">
                          {t('pages.ChallengeDispute.customer_purchase_ip')}
                        </span>
                        <span className="text-sm text-gray-900" data-id="element-6254">
                          192.168.1.1
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>}
    </div>;
}